import React, { useState, useRef, useMemo, useEffect } from "react";
import {
    Tabs, Tab, Box, Typography, TextField, Button, Accordion,
    AccordionSummary, AccordionDetails, Grid, Select,
    MenuItem, Alert, CircularProgress, Paper, FormControlLabel, Switch, LinearProgress, Fade, Grow, Zoom, Popover, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { runBessTask, runEcosOp, getSOH, progress, plot_data, daily_maximum, average_curve, load_factor } from "../api/Bess";
import {
    LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, ReferenceArea,
    Tooltip, Legend, ResponsiveContainer, ComposedChart, AreaChart, Area, Brush, ScatterChart, Scatter,
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Divider } from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Bolt as BoltIcon,
    Speed as SpeedIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/HelpOutline';

function InfoTooltip({ text, sx }) {
    const [anchorEl, setAnchorEl] = useState(null);

    return (
        <>
            <InfoIcon
                fontSize="1%"
                sx={{ color: "#747474", cursor: "pointer", ...sx }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                disableRestoreFocus
                sx={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: "-8px",
                            p: 1.5,
                            maxWidth: 220,
                            borderRadius: "8px",
                            backgroundColor: "#3a3a3a",
                            color: "#fff",
                            boxShadow: "0px 4px 14px rgba(0,0,0,0.25)",
                        }
                    }
                }}
            >
                <Typography sx={{ fontSize: "0.8rem" }}>
                    {text}
                </Typography>
            </Popover>
        </>
    );
}


function BESS_Designer() {
    // File upload states
    const [file, setFile] = useState(null);
    const [curveplotfile, setCurveplotFile] = useState(null);
    const [error, setError] = useState("");
    const [sohFile, setSohFile] = useState(null);
    const [sohError, setSohError] = useState("");
    const fileInputRef = useRef();
    const [tabValue, setTabValue] = useState(0);
    const sohFileInputRef = useRef();
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    // Form data state
    const [formData, setFormData] = useState({
        client_name: "Amsolar",
        chosen_bess_kWh: 5015,
        BESS_RTE_charge_efficiency: 0.95,
        BESS_RTE_discharge_efficiency: 0.95,
        batt_max_SOC: 100,
        batt_min_SOC: 10,
        C_rating: 0.5,
        capex_rm_per_kWh: 1100,
        operating_cost_percentage: 1.5,
        tariff_before_bess: "MV-TOU",
        tariff_after_bess: "MV-TOU",
        rm_per_kwh_afa_before: 0.0,
        rm_per_kwh_afa_after: 0.0,
        rm_per_kwh_nem_before: 0.0,
        rm_per_kwh_nem_after: 0.0,
        re_pctg_before: 1.6,
        re_pctg_after: 1.6,
        rm_per_kwh_eei_before: 0.0,
        rm_per_kwh_eei_after: 0.0,
        min_bess_kWh: 1,
        max_bess_kWh: 10000,
        bess_step_size_kWh: 100,
        start_time: "00:00",
        end_time: "23:30",
        include_weekends: "true",
        chosen_system_life: 10,
        chosen_soh_degradation: 2.5

    });
    const FIELD_MAX = {
        chosen_system_life: 25,
        chosen_soh_degradation: 10,
    };

    // API states - using bessResult, ecosResult, sohResult (not result)
    const [bessResult, setBessResult] = useState(null);
    const [ecosResult, setEcosResult] = useState(null);
    const [sohResult, setSohResult] = useState(null);
    const [loading, setLoading] = useState({
        bess: false,
        ecos: false,
        soh: false
    });
    const [apiError, setApiError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [progressData, setprogressData] = useState([]);
    const [charttData, setChartData] = useState(null);
    const [curveplotData, setcurveplotData] = useState(null);
    const [dailymax, setDailymax] = useState(null);
    const [averagecurve, setAveragecurve] = useState(null);
    const [loadfactor, setLoadfactor] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 1. Update handleBrowseClick - reset before opening dialog
    const handleBrowseClick = (event) => {
        // Prevent event bubbling if needed
        if (event) {
            event.stopPropagation();
        }

        // Reset the file input value before opening the dialog
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
        fileInputRef.current.click();
    };

    // 2. Update handleSohBrowseClick - reset before opening dialog
    const handleSohBrowseClick = (event) => {
        // Prevent event bubbling if needed
        if (event) {
            event.stopPropagation();
        }

        // Reset the file input value before opening the dialog
        if (sohFileInputRef.current) {
            sohFileInputRef.current.value = null;
        }
        sohFileInputRef.current.click();
    };

    // 3. Update handleFileChange - reset after selection
    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        // Check file type
        if (!selectedFile.name.endsWith(".csv") && !selectedFile.name.endsWith(".xlsx") && !selectedFile.name.endsWith(".xls")) {
            setError("Please upload a CSV or Excel file.");
            setFile(null);
            // Reset the input
            event.target.value = null;
            return;
        }

        // Check file size (200MB limit)
        if (selectedFile.size > 200 * 1024 * 1024) {
            setError("File is too large. Max 200MB.");
            setFile(null);
            // Reset the input
            event.target.value = null;
            return;
        }

        setError("");
        setFile(selectedFile);
        setBessResult(null);
        setEcosResult(null);
        setApiError(null);

        // Reset the input value after successful selection
        event.target.value = null;
    };

    //cruve plotter file change
    const handlecurveplotFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        // Check file type
        if (!selectedFile.name.endsWith(".csv") && !selectedFile.name.endsWith(".xlsx") && !selectedFile.name.endsWith(".xls")) {
            setError("Please upload a CSV or Excel file.");
            setCurveplotFile(null);
            // Reset the input
            event.target.value = null;
            return;
        }

        // Check file size (200MB limit)
        if (selectedFile.size > 200 * 1024 * 1024) {
            setError("File is too large. Max 200MB.");
            setCurveplotFile(null);
            // Reset the input
            event.target.value = null;
            return;
        }

        setError("");
        setCurveplotFile(selectedFile);
        setBessResult(null);
        setEcosResult(null);
        setApiError(null);

        // Reset the input value after successful selection
        event.target.value = null;
    };


    // 4. Update handleSohFileChange - reset after selection
    // const handleSohFileChange = (event) => {
    //     const selectedFile = event.target.files[0];
    //     if (!selectedFile) return;

    //     // Check file type
    //     if (!selectedFile.name.endsWith(".csv") && !selectedFile.name.endsWith(".xlsx") && !selectedFile.name.endsWith(".xls")) {
    //         setSohError("Please upload a CSV or Excel file.");
    //         setSohFile(null);
    //         // Reset the input
    //         event.target.value = null;
    //         return;
    //     }

    //     // Check file size (200MB limit)
    //     if (selectedFile.size > 200 * 1024 * 1024) {
    //         setSohError("File is too large. Max 200MB.");
    //         setSohFile(null);
    //         // Reset the input
    //         event.target.value = null;
    //         return;
    //     }

    //     setSohError("");
    //     setSohFile(selectedFile);
    //     setSohResult(null);

    //     // Reset the input value after successful selection
    //     event.target.value = null;
    // };
    // Clear messages
    const clearMessages = () => {
        setApiError(null);
        setSuccessMessage(null);
    };

    // Prepare form data for API calls
    const prepareFormData = (includeChosenBess = false) => {
        const dataToSend = {
            client_name: formData.client_name,
            BESS_RTE_charge_efficiency: parseFloat(formData.BESS_RTE_charge_efficiency),
            BESS_RTE_discharge_efficiency: parseFloat(formData.BESS_RTE_discharge_efficiency),
            batt_max_SOC: parseFloat(formData.batt_max_SOC),
            batt_min_SOC: parseFloat(formData.batt_min_SOC),
            C_rating: parseFloat(formData.C_rating),
            capex_rm_per_kWh: parseFloat(formData.capex_rm_per_kWh),
            operating_cost_percentage: parseFloat(formData.operating_cost_percentage),
            tariff_before_bess: formData.tariff_before_bess,
            tariff_after_bess: formData.tariff_after_bess,
            rm_per_kwh_afa_before: parseFloat(formData.rm_per_kwh_afa_before),
            rm_per_kwh_afa_after: parseFloat(formData.rm_per_kwh_afa_after),
            rm_per_kwh_nem_before: parseFloat(formData.rm_per_kwh_nem_before),
            rm_per_kwh_nem_after: parseFloat(formData.rm_per_kwh_nem_after),
            re_pctg_before: parseFloat(formData.re_pctg_before),
            re_pctg_after: parseFloat(formData.re_pctg_after),
            rm_per_kwh_eei_before: parseFloat(formData.rm_per_kwh_eei_before),
            rm_per_kwh_eei_after: parseFloat(formData.rm_per_kwh_eei_after),
            min_bess_kWh: parseFloat(formData.min_bess_kWh),
            max_bess_kWh: parseFloat(formData.max_bess_kWh),
            bess_step_size_kWh: parseFloat(formData.bess_step_size_kWh)
        };

        if (includeChosenBess) {
            dataToSend.chosen_bess_kWh = parseFloat(formData.chosen_bess_kWh);
            dataToSend.chosen_system_life = parseFloat(formData.chosen_system_life);
            dataToSend.chosen_soh_degradation = parseFloat(formData.chosen_soh_degradation);
        }

        return dataToSend;
    };

    // 1. BESS Sizing API - uses CSV file only
    const handleTamingBESS = async () => {
        clearMessages();

        if (!file) {
            setApiError("Please upload a CSV file first");
            return;
        }

        const formDataObj = new FormData();
        formDataObj.append("file", file);
        formDataObj.append("data", JSON.stringify(prepareFormData(false)));

        try {
            setLoading(prev => ({ ...prev, bess: true }));

            // runBessTask already returns response.data directly
            const result = await runBessTask(formDataObj);

            setBessResult(result);

            // Show success message with or without task_id
            const task_id = result?.task_id;
            if (task_id) {
                await pollProgressUntilComplete(task_id);

                const Response = await progress(task_id);
                setprogressData(Response?.data || []);

                setSuccessMessage(`BESS sizing task submitted successfully! Task ID: ${task_id}`);
            } else {
                setSuccessMessage("BESS sizing task submitted successfully!");
            }

        } catch (err) {
            console.error("BESS sizing error:", err);
            setApiError("BESS sizing failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(prev => ({ ...prev, bess: false }));
        }
    };

    // Polling function that continues until task is complete
    const pollProgressUntilComplete = async (task_id) => {
        let isComplete = false;
        let attempts = 0;
        const maxAttempts = 60;

        while (!isComplete && attempts < maxAttempts) {
            try {

                const progressResponse = await progress(task_id);


                const progressData = progressResponse.data;

                // Update progress data in state
                setprogressData(progressData);

                // Check if task is complete based on state
                if (progressData.state === "SUCCESS") {


                    // The results are inside progressData
                    // You can access them directly from progressData
                    setSuccessMessage("Task processing completed!");

                    isComplete = true;
                    break;
                }

                // Check if task failed
                if (progressData.state === "FAILURE") {
                    console.error("Task failed:", progressData);
                    setApiError(`Task failed: ${progressData.status || "Unknown error"}`);
                    isComplete = true;
                    break;
                }

                // Wait before next poll
                await new Promise(resolve => setTimeout(resolve, 3000));
                attempts++;

            } catch (err) {
                console.error("Error polling progress:", err);
                await new Promise(resolve => setTimeout(resolve, 5000));
                attempts++;
            }
        }

        if (attempts >= maxAttempts) {
            setApiError("Task is taking too long. Please check back later.");
        }
    };

    const prepareECOSData = () => {
        const dataToSend = {
            client_name: formData.client_name,
            BESS_RTE_charge_efficiency: parseFloat(formData.BESS_RTE_charge_efficiency),
            BESS_RTE_discharge_efficiency: parseFloat(formData.BESS_RTE_discharge_efficiency),
            batt_max_SOC: parseFloat(formData.batt_max_SOC),
            batt_min_SOC: parseFloat(formData.batt_min_SOC),
            C_rating: parseFloat(formData.C_rating),
            capex_rm_per_kWh: parseFloat(formData.capex_rm_per_kWh),
            operating_cost_percentage: parseFloat(formData.operating_cost_percentage),
            tariff_before_bess: formData.tariff_before_bess,
            tariff_after_bess: formData.tariff_after_bess,
            rm_per_kwh_afa_before: parseFloat(formData.rm_per_kwh_afa_before),
            rm_per_kwh_afa_after: parseFloat(formData.rm_per_kwh_afa_after),
            rm_per_kwh_nem_before: parseFloat(formData.rm_per_kwh_nem_before),
            rm_per_kwh_nem_after: parseFloat(formData.rm_per_kwh_nem_after),
            re_pctg_before: parseFloat(formData.re_pctg_before),
            re_pctg_after: parseFloat(formData.re_pctg_after),
            rm_per_kwh_eei_before: parseFloat(formData.rm_per_kwh_eei_before),
            rm_per_kwh_eei_after: parseFloat(formData.rm_per_kwh_eei_after),
            min_bess_kWh: parseFloat(formData.min_bess_kWh),
            max_bess_kWh: parseFloat(formData.max_bess_kWh),
            bess_step_size_kWh: parseFloat(formData.bess_step_size_kWh),
            chosen_bess_kWh: parseFloat(formData.chosen_bess_kWh),
            chosen_system_life: parseFloat(formData.chosen_system_life),
            chosen_soh_degradation: parseFloat(formData.chosen_soh_degradation)

        };

        return dataToSend;
    };

    // 2. eCOS Operation API - uses same CSV + chosen_bess_kWh value
    const handleECOS = async () => {
        clearMessages();

        if (!file) {
            setApiError("Please upload a CSV file first");
            return;
        }

        const formDataObj = new FormData();
        formDataObj.append("file", file);
        formDataObj.append("data", JSON.stringify(prepareECOSData(true))); // Include chosen_bess_kWh

        try {
            setLoading(prev => ({ ...prev, ecos: true }));

            const result = await runEcosOp(formDataObj);  // This is already response.data


            setEcosResult(result.message);  // Remove .data here
            setSohResult(result.soh_degradation);  // Remove .data here
            setSuccessMessage("eCOS operation completed successfully!");

        } catch (err) {
            console.error("eCOS operation error:", err);
            setApiError("eCOS operation failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(prev => ({ ...prev, ecos: false }));
        }
    };

    // 3. SOH Degradation API - uses two CSVs (main + SOH) + same data as eCOS
    // const handleSOH = async () => {
    //     clearMessages();

    //     if (!file) {
    //         setApiError("Please upload the main CSV file first");
    //         return;
    //     }

    //     if (!sohFile) {
    //         setApiError("Please upload an SOH CSV file");
    //         return;
    //     }

    //     const formDataObj = new FormData();
    //     formDataObj.append("file", file); // Main CSV from step 1
    //     formDataObj.append("soh", sohFile); // SOH CSV
    //     formDataObj.append("data", JSON.stringify(prepareECOSData(true))); // Same data as eCOS

    //     try {
    //         setLoading(prev => ({ ...prev, soh: true }));

    //         const response = await getSOH(formDataObj);

    //         setSohResult(response.message);
    //         setSuccessMessage("SOH degradation modelling completed successfully!");
    //     } catch (err) {
    //         console.error("SOH degradation error:", err);
    //         setApiError("SOH degradation failed: " + (err.response?.data?.detail || err.message));
    //     } finally {
    //         setLoading(prev => ({ ...prev, soh: false }));
    //     }
    // };


    //the data need to send to the curve plotter api
    const preparecurveData = () => {
        const dataToSend = {
            client_name: formData.client_name,
            tariff_before_bess: formData.tariff_before_bess,  // <-- remove parseFloat     
        };
        return dataToSend;
    };

    // ✅ place here (same level as curveplot)
    const createFormData = () => {
        const fd = new FormData();
        fd.append("file", curveplotfile);
        fd.append("data", JSON.stringify(preparecurveData()));
        return fd;
    };



    //curve plotter api call
    const curveplot = async () => {
        clearMessages();

        if (!curveplotfile) {
            setApiError("Please upload a CSV file first");
            return;
        }

        try {
            setLoading(prev => ({ ...prev, bess: true }));

            // 🔥 create fresh FormData each time
            const result = await plot_data(createFormData());
            setcurveplotData(result);

            const resultmax = await daily_maximum(createFormData());
            setDailymax(resultmax);

            const resultavg = await average_curve(createFormData());
            setAveragecurve(resultavg);

            const { include_weekends, start_time, end_time } = formData;

            const resultload = await load_factor(createFormData(), {
                include_weekends,
                start_time,
                end_time
            });

            setLoadfactor(resultload);

        } catch (err) {
            console.error("BESS sizing error:", err);
            setApiError(
                "BESS sizing failed: " +
                (err.response?.data?.detail || err.message)
            );
        } finally {
            setLoading(prev => ({ ...prev, bess: false }));
        }
    };


    // Transform data for the BESS Analysis chart
    const chartData = progressData?.result?.bess_size_kWh?.map((size, index) => {
        const yearlySavings = progressData.result?.yearly_MD_savings_rm?.[index] || 0;
        return {
            bessSize: size,
            roi: progressData.result?.roi_yr?.[index] || 0,
            incSavingsPerKwh: progressData.result?.inc_savings_per_kwh?.[index] || 0,
            yearlySavings: yearlySavings,
            mdShaved: yearlySavings / 12 / 97.06
        };
    }) || [];

    // Helper function to generate time labels for 30-minute intervals
    const generateTimeLabels = () => {
        const labels = [];
        for (let hour = 0; hour < 24; hour++) {
            // Add 00 minutes
            labels.push(`${hour.toString().padStart(2, '0')}:00`);
            // Add 30 minutes (except for the last hour)
            if (hour < 23) {
                labels.push(`${hour.toString().padStart(2, '0')}:30`);
            }
        }
        // Add the last 30-minute mark (23:30)
        labels.push('23:30');
        return labels;
    };
    // Transform eCOS data for charts
    const prepareEcosChartData = (ecosData) => {
        if (!ecosData) return [];

        const timeLabels = generateTimeLabels();
        const bessSize = ecosData.bess_size_kWh || 5015;

        return timeLabels.map((time, index) => ({
            time: time,
            netLoadBefore: ecosData.net_load_before_kW?.[index] || 0,
            netLoadAfter: ecosData.net_load_after_kW?.[index] || 0,
            battPower: ecosData.batt_power_kW?.[index] || 0,
            battPowerKwh: ecosData.batt_power_kWh?.[index] || 0,
            // Calculate SOC: (batt_power_kWh / bess_size_kWh) * 100%
            batterySOC: ((ecosData.batt_power_kWh?.[index] || 0) / bessSize) * 100
        }));
    };

    // Custom Tooltip Component
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper sx={{ p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Time: {label}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, backgroundColor: entry.color, borderRadius: '50%' }} />
                            <Typography variant="body2">
                                {entry.name}: {entry.value?.toFixed?.(2) ?? entry.value}
                            </Typography>
                        </Box>
                    ))}
                </Paper>
            );
        }
        return null;
    };

    const CustomTooltipp = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>Time: {label}</p>
                    {payload.map((entry, index) => {
                        let color = entry.color;
                        // Battery Power's color depends on charging vs discharging
                        if (entry.dataKey === 'battPower') {
                            color = entry.value >= 0 ? '#4caf50' : '#ff5252';
                        }
                        return (
                            <p key={index} style={{ margin: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: entry.dataKey === 'batterySOC' ? '50%' : '2px',
                                        backgroundColor: color,
                                    }}
                                />
                                {entry.name}: {entry.value?.toFixed ? entry.value.toFixed(2) : entry.value}
                            </p>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Transform SOH data for chart
    const prepareSOHChartData = (sohData) => {
        if (!sohData || !Array.isArray(sohData)) return [];

        return sohData.map(item => ({
            year: item.year,
            usableEnergy: item.usable_energy_at_POC_kWh?.toFixed(2) || 0,
            monthlyMDSavings: item.MD_savings_rm_per_mth || 0,
            // Calculate MD Shaved (kW reduction)
            mdShaved: (item.MD_before_kW - item.MD_after_kW) || 0
        }));
    };

    // Add these style constants inside your Calculator component, before the return statement
    const tableHeaderStyle = {
        padding: '12px',
        textAlign: 'left',
        fontWeight: 'bold',
        borderBottom: '2px solid #ddd',
        position: 'sticky',
        top: 0,
        backgroundColor: '#f5f5f5'
    };

    const tableCellStyle = {
        padding: '10px 12px',
        textAlign: 'left'
    };

    //export excel
    const exportSOHToExcel = () => {
        if (!sohResult || !Array.isArray(sohResult) || sohResult.length === 0) {
            alert('No SOH data to export');
            return;
        }

        // Prepare the data for export
        const exportData = sohResult.map(item => ({
            'Year': item.year,
            'Usable Energy at POC (kWh)': item.usable_energy_at_POC_kWh?.toFixed(2) || 0,
            'MD Before (kW)': item.MD_before_kW || 0,
            'MD After (kW)': item.MD_after_kW || 0,
            'MD Shaved (kW)': (item.MD_before_kW - item.MD_after_kW) || 0,
            'Monthly MD Savings (RM)': item.MD_savings_rm_per_mth || 0,
            'Annual MD Savings (RM)': (item.MD_savings_rm_per_mth * 12) || 0
        }));

        // Add summary row
        const summaryData = {
            'Year': 'SUMMARY',
            'Usable Energy at POC (kWh)': sohResult[0]?.usable_energy_at_POC_kWh?.toFixed(2) || 0,
            'MD Before (kW)': sohResult[0]?.MD_before_kW || 0,
            'MD After (kW)': sohResult[0]?.MD_after_kW || 0,
            'MD Shaved (kW)': (sohResult[0]?.MD_before_kW - sohResult[0]?.MD_after_kW) || 0,
            'Monthly MD Savings (RM)': sohResult[0]?.MD_savings_rm_per_mth || 0,
            'Annual MD Savings (RM)': (sohResult[0]?.MD_savings_rm_per_mth * 12) || 0
        };

        // Add degradation calculation
        const firstYear = sohResult[0]?.usable_energy_at_POC_kWh || 0;
        const lastYear = sohResult[sohResult.length - 1]?.usable_energy_at_POC_kWh || 0;
        const totalDegradation = ((firstYear - lastYear) / firstYear * 100).toFixed(2);

        const degradationRow = {
            'Year': `Total Degradation: ${totalDegradation}%`,
            'Usable Energy at POC (kWh)': `${firstYear.toFixed(2)} → ${lastYear.toFixed(2)}`,
            'MD Before (kW)': '-',
            'MD After (kW)': '-',
            'MD Shaved (kW)': '-',
            'Monthly MD Savings (RM)': '-',
            'Annual MD Savings (RM)': '-'
        };

        // Combine all data
        const finalExportData = [
            ...exportData,
            {},
            { 'Year': '--- SUMMARY ---' },
            summaryData,
            degradationRow
        ];

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(finalExportData);

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'SOH Degradation');

        // Generate Excel file
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

        // Save file with timestamp
        const date = new Date();
        const timestamp = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        saveAs(blob, `SOH_Degradation_${timestamp}.xlsx`);
    };

    // Process CSV file and generate chart data
    const processFile = () => {
        if (!file) {
            setError("Please upload a CSV file first");
            return;
        }

        setLoading(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const content = e.target.result;
                const lines = content.split('\n');
                const headers = lines[0].split(',');

                // Parse data (assuming first column is time/index, second column is value)
                const parsedData = [];
                for (let i = 1; i < lines.length; i++) {
                    if (lines[i].trim()) {
                        const values = lines[i].split(',');
                        parsedData.push({
                            index: i - 1,
                            time: values[0] || `Point ${i - 1}`,
                            value: parseFloat(values[1]) || 0
                        });
                    }
                }

                setChartData(parsedData);
                setError("");
            } catch (err) {
                setError("Error parsing file: " + err.message);
                setChartData(null);
            } finally {
                setLoading(false);
            }
        };

        reader.onerror = () => {
            setError("Error reading file");
            setLoading(false);
        };

        reader.readAsText(file);
    };

    const data = prepareEcosChartData(ecosResult);

    const peakStart = "14:00";
    const peakEnd = "22:00";

    //Convert time strings to minutes to let user cannot choose the end_time before start_time
    const timeToMinutes = (timeStr) => {
        const [h, m] = timeStr.split(":").map(Number);
        return h * 60 + m;
    };

    const hasValidationErrors = Object.entries(FIELD_MAX).some(
        ([field, max]) => formData[field] > max
    );


    //plotdata graph 
    // ✅ 1. FORMAT RAW DATA — now handles all 3 series
    const formattedCurveData = useMemo(() => {
        if (!curveplotData) return [];

        const consumption = curveplotData["Consumption (kW)"] ?? [];
        const consumptionAfterPV = curveplotData["Consumption after PV (kW)"] ?? [];
        const pv = curveplotData["PV (kW)"] ?? [];

        if (consumption.length === 0) return [];

        return consumption.map((item, i) => ({
            time: item.time,
            date: item.time.split("T")[0],
            consumption: Number(item.value) || 0,
            consumptionAfterPV: Number(consumptionAfterPV[i]?.value) || 0,
            pv: Number(pv[i]?.value) || 0,
        }));
    }, [curveplotData]);

    // ✅ 2. GROUP BY DAY (for chart)
    const dailyData = useMemo(() => {
        const grouped = {};

        formattedCurveData.forEach((item) => {
            if (!grouped[item.date]) {
                grouped[item.date] = { consumption: [], consumptionAfterPV: [], pv: [] };
            }
            grouped[item.date].consumption.push(item.consumption);
            grouped[item.date].consumptionAfterPV.push(item.consumptionAfterPV);
            grouped[item.date].pv.push(item.pv);
        });

        return Object.keys(grouped).map((date) => {
            const g = grouped[date];
            const avg = (arr) => arr.reduce((sum, v) => sum + v, 0) / (arr.length || 1);

            return {
                date,
                consumption: Number(avg(g.consumption).toFixed(2)),
                consumptionAfterPV: Number(avg(g.consumptionAfterPV).toFixed(2)),
                pv: Number(avg(g.pv).toFixed(2)),
            };
        });
    }, [formattedCurveData]);

    // ✅ 3. SUMMARY — based on consumption
    const min = Math.min(...formattedCurveData.map(d => d.consumption || 0));
    const max = Math.max(...formattedCurveData.map(d => d.consumption || 0));
    const avg = formattedCurveData.reduce((sum, d) => sum + (d.consumption || 0), 0)
        / (formattedCurveData.length || 1);
    //daily max data for table and export
    const formattedDailyMax = useMemo(() => {
        if (!dailymax) return [];

        return dailymax.map(d => {
            const date = new Date(d.time_as_datetime);

            return {
                time: date.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit"
                }),
                power: d.cws_kW !== null ? Number(d.cws_kW) : null
            };
        });
    }, [dailymax]);

    //average curve data for table and export
    const prepareAverageCurveData = (data) => {
        if (!data || !data.weekday || !data.weekend) return [];

        return data.weekday.time.map((time, i) => {
            const wLow = data.weekday.minus_1_sigma[i] ?? 0;
            const wHigh = data.weekday.plus_1_sigma[i] ?? 0;
            const weLow = data.weekend.minus_1_sigma[i] ?? 0;
            const weHigh = data.weekend.plus_1_sigma[i] ?? 0;

            return {
                time: time?.slice(0, 5) ?? "",
                // Average Lines
                weekday_avg: data.weekday.avg[i] ?? 0,
                weekend_avg: data.weekend.avg[i] ?? 0,
                // Range Bands: Passed as [Bottom, Top] to avoid stacking errors
                weekday_range: [wLow, wHigh],
                weekend_range: [weLow, weHigh],
            };
        });
    };

    // Helper to format numbers to 2 decimal places
    const formatNum = (num) => (num !== null && num !== undefined ? Number(num).toFixed(2) : "0.00");


    const COLORS = {
        primary: "#7e4a9d",
        primaryLight: "#b39ddb",
        primaryDark: "#4a2c6a",

        weekday: "#42a5f5",
        weekdayLight: "#90caf9",
        weekdayDark: "#1565c0",

        weekend: "#ffa726",
        weekendLight: "#ffcc80",
        weekendDark: "#ef6c00",

        high: "#2e7d32",
        low: "#d32f2f",

        border: "#e0e0e0",
        bg: "#f6f3fb",
    };

    // Add this state
    const [animatedPercentage, setAnimatedPercentage] = useState(0);

    // Add useEffect for smooth counting
    useEffect(() => {
        if (progressData?.percentage !== undefined) {
            const targetValue = progressData.percentage;
            const startValue = animatedPercentage;
            const difference = targetValue - startValue;

            // If it's a big jump (like from 57 to 100), animate through all numbers
            if (Math.abs(difference) > 20) {
                const duration = 1500; // 1.5 seconds for big jump
                const steps = 60; // 60 steps (60fps)
                const increment = difference / steps;
                let currentStep = 0;

                const interval = setInterval(() => {
                    currentStep++;
                    const newValue = Math.min(
                        Math.floor(startValue + (increment * currentStep)),
                        targetValue
                    );
                    setAnimatedPercentage(newValue);

                    if (currentStep >= steps || newValue >= targetValue) {
                        clearInterval(interval);
                        setAnimatedPercentage(targetValue);
                    }
                }, duration / steps);

                return () => clearInterval(interval);
            }
            // Normal smooth transition for small increments
            else if (difference !== 0) {
                const duration = 500;
                const startTime = performance.now();
                const startVal = animatedPercentage;

                const animate = (currentTime) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeOutCubic = 1 - Math.pow(1 - progress, 3);
                    const currentValue = startVal + (targetValue - startVal) * easeOutCubic;

                    setAnimatedPercentage(Math.round(currentValue));

                    if (progress < 1) {
                        requestAnimationFrame(animate);
                    }
                };

                requestAnimationFrame(animate);
            }
        }
    }, [progressData?.percentage]);

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleInfoEnter = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleInfoLeave = () => {
        setAnchorEl(null);
    };

    const handleDownloadTemplate = () => {
        const link = document.createElement("a");
        link.href = "/templates/one_day_sizing_template.csv"; // path to your actual file
        link.setAttribute("download", "one_day_sizing_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadSOHTemplate = () => {
        const link = document.createElement("a");
        link.href = "/templates/soh_degradation_profile_template.csv"; // adjust path to your actual file
        link.setAttribute("download", "soh_degradation_profile_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadTemplate = () => {
        const link = document.createElement("a");
        link.href = "/templates/curve_plotter_template.csv"; // adjust path/filename to your actual file
        link.setAttribute("download", "curve_plotter_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    //average weekday curve
    const DAY_COLORS = {
        Monday: "#1f77b4",
        Tuesday: "#ff7f0e",
        Wednesday: "#2ca02c",
        Thursday: "#d62728",
        Friday: "#9467bd",
        Saturday: "#8c564b",
        Sunday: "#e377c2",
    };

    const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const prepareDailyCurveData = (data) => {
        if (!data || typeof data !== "object") return [];

        // Filter only valid day keys that have arrays
        const dayData = Object.fromEntries(
            Object.entries(data).filter(
                ([key, val]) => DAY_ORDER.includes(key) && Array.isArray(val)
            )
        );

        if (Object.keys(dayData).length === 0) return [];

        // Collect all unique times safely
        const allTimes = [...new Set(
            Object.values(dayData)
                .flat()
                .filter(d => d && typeof d.time === "string")  // ← must be a string
                .map(d => d.time)
        )].sort();

        if (allTimes.length === 0) return [];

        return allTimes.map(time => {
            const row = { time: time.slice(0, 5) };  // safe now — all are strings
            DAY_ORDER.forEach(day => {
                const match = dayData[day]?.find(d => d?.time === time);
                row[day] = match?.cws != null ? Number(Number(match.cws).toFixed(2)) : null;
            });
            return row;
        });
    };


    return (
        <Box sx={{ width: "100%", pt: "2%", backgroundColor: "white", minHeight: "100vh", pl: "3%", pr: "3%", pb: "2%" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab
                    label="One-Day-Sizing"
                    sx={{
                        color: "black",
                        fontSize: "1.5rem",   // similar to h4
                        fontWeight: "bold",
                        textTransform: "none", // remove uppercase
                    }}
                />
                {/* <Tab
                    label="Curve Plotter"
                    sx={{
                        color: "black",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        textTransform: "none",
                    }}
                /> */}
            </Tabs>


            {/* Status Messages */}
            {apiError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
                    {apiError}
                </Alert>
            )}

            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}
            {tabValue === 0 && (
                <Box>
                    {/* 1-Day Sizing */}
                    <Accordion defaultExpanded sx={{ "&::before": { display: "none" }, borderRadius: "0.5rem", padding: "0.5%", border: "1px solid #d8d8d8" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography sx={{ fontSize: "1rem" }}>1: Client Name and 1-Day Data</Typography>

                            </div>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "2%", width: "100%" }}>
                                {/* Client Name */}
                                <Grid item xs={12} sm={3.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Client Name : </Typography>
                                        <TextField
                                            name="client_name"
                                            value={formData.client_name}
                                            onChange={handleInputChange}
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Current Tariff */}
                                <Grid item xs={12} sm={3.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Current Tariff : </Typography>
                                        <Select
                                            name="tariff_before_bess"
                                            value="MV-TOU"
                                            disabled
                                            fullWidth
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                "&.Mui-disabled": {
                                                    "& .MuiSelect-select": { WebkitTextFillColor: "rgba(0, 0, 0, 0.8)" }
                                                }
                                            }}
                                        >
                                            <MenuItem value="MV-TOU">MV-TOU</MenuItem>
                                            {/* <MenuItem value="MV-Water & Sewerage Operator MV-TOU">Water & Sewerage Operator MV-TOU</MenuItem>
                                            <MenuItem value="MV-General">MV-General</MenuItem>
                                            <MenuItem value="HV-TOU">HV-TOU</MenuItem> */}
                                        </Select>
                                    </Box>
                                </Grid>
                                {/* Input from MRS */}
                                <Grid item xs={12} sm={3.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Input from MRS :
                                            <InfoTooltip sx={{ ml: "1%" }} text="Link the One-Day-Sizing data from MRS" />
                                        </Typography>
                                        <Select
                                            name="Input From MRS"
                                            value="Disable"
                                            disabled
                                            fullWidth
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                "&.Mui-disabled": {
                                                    "& .MuiSelect-select": { WebkitTextFillColor: "rgba(0, 0, 0, 0.8)" }
                                                }
                                            }}
                                        >
                                            <MenuItem value="Disable">Disable</MenuItem>
                                            {/* <MenuItem value="MV-Water & Sewerage Operator MV-TOU">Water & Sewerage Operator MV-TOU</MenuItem>
                                            <MenuItem value="MV-General">MV-General</MenuItem>
                                            <MenuItem value="HV-TOU">HV-TOU</MenuItem> */}
                                        </Select>
                                    </Box>
                                </Grid>

                                {/* AFA Surcharge */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>AFA Surcharge (RM/kWh) : </Typography>
                                        <TextField
                                            name="rm_per_kwh_afa_before"
                                            value={formData.rm_per_kwh_afa_before}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* NEM Rebate */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>NEM Rebate (RM/kWh)</Typography>
                                        <TextField
                                            name="rm_per_kwh_nem_before"
                                            value={formData.rm_per_kwh_nem_before}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* EEI Rebate */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>EEI Rebate (RM/kWh)</Typography>
                                        <TextField
                                            name="rm_per_kwh_eei_before"
                                            value={formData.rm_per_kwh_eei_before}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* RE Charge */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>RE Charge (%) : </Typography>
                                        <TextField
                                            name="re_pctg_before"
                                            value={formData.re_pctg_before}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Select Date */}
                                {/* <Grid item xs={12} sm={2.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Select Date</Typography>
                                        <TextField
                                            type="date"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid> */}
                            </Grid>

                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                fullWidth
                                onClick={handleDownloadTemplate}
                                sx={{
                                    backgroundColor: "#7E4A9D",
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    height: "40px",
                                    fontWeight: 500,
                                    "&:hover": { backgroundColor: "#6A3490" }
                                }}
                            >
                                Download CSV Template
                            </Button>

                            {/* CSV Upload */}
                            <Box sx={{ border: "1px dashed #ccc", borderRadius: 2, p: 3, backgroundColor: "#fafafa" }}>
                                <Typography sx={{ fontWeight: "bold", mb: 1 }}>Upload your filled CSV here</Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                        p: 3,
                                        backgroundColor: "#fff",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                    }}
                                    onClick={handleBrowseClick}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 40, color: "#999" }} />
                                    <Typography color="textSecondary">Drag and drop file here</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Limit 200MB per file • CSV, Excel
                                    </Typography>
                                    <Button variant="contained" sx={{ mt: 2 }} onClick={handleBrowseClick}>
                                        Browse files
                                    </Button>
                                    <input
                                        type="file"
                                        accept=".csv,.xlsx,.xls"
                                        ref={fileInputRef}
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                    />
                                </Box>
                                {error && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {error}
                                    </Typography>
                                )}
                                {file && (
                                    <Typography color="primary" sx={{ mt: 1 }}>
                                        Selected file: {file.name}
                                    </Typography>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* 2: BESS Sizing */}
                    <Accordion sx={{ "&::before": { display: "none" }, mt: "1%", borderRadius: "0.5rem", padding: "0.5%", border: "1px solid #d8d8d8" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography sx={{ fontSize: "1rem" }}>2: BESS Sizing</Typography>
                            </div>
                        </AccordionSummary>

                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            {/* Tariff Rate*/}
                            <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>💰Tariff Rate</Typography>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "2%", width: "100%" }}>
                                {/* Tariff Rate - Select Tariff with BESS*/}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Select Tariff with BESS : </Typography>
                                        <Select
                                            name="tariff_after_bess"
                                            value="MV-TOU"
                                            disabled
                                            fullWidth
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                "&.Mui-disabled": {
                                                    "& .MuiSelect-select": { WebkitTextFillColor: "rgba(0, 0, 0, 0.8)" }
                                                }
                                            }}
                                        >
                                            <MenuItem value="MV-TOU">MV-TOU</MenuItem>
                                            {/* <MenuItem value="MV-Water & Sewerage Operator MV-TOU">Water & Sewerage Operator MV-TOU</MenuItem>
            <MenuItem value="MV-General">MV-General</MenuItem>
            <MenuItem value="HV-TOU">HV-TOU</MenuItem> */}
                                        </Select>
                                    </Box>
                                </Grid>

                                {/* Empty grid item */}
                                <Grid item xs={12} sm={5.85} />

                                {/* AFA Surcharge After */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>AFA Surcharge (RM/kWh) : </Typography>
                                        <TextField
                                            name="rm_per_kwh_afa_after"
                                            value={formData.rm_per_kwh_afa_after}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* NEM Rebate After */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>NEM Rebate (RM/kWh)</Typography>
                                        <TextField
                                            name="rm_per_kwh_nem_after"
                                            value={formData.rm_per_kwh_nem_after}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* EEI Rebate After */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>EEI Rebate (RM/kWh)</Typography>
                                        <TextField
                                            name="rm_per_kwh_eei_after"
                                            value={formData.rm_per_kwh_eei_after}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* RE Charge After */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>RE Charge (%) : </Typography>
                                        <TextField
                                            name="re_pctg_after"
                                            value={formData.re_pctg_after}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* BESS Parameters*/}
                            <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>🔋BESS Parameters</Typography>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "1.95%", width: "100%" }}>
                                {/* Charging Efficiency*/}
                                <Grid item xs={12} sm={1.79}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Charging Efficiency
                                            <InfoTooltip sx={{ ml: "2%" }} text="Percentage of energy retained during charging." />
                                        </Typography>
                                        <TextField
                                            name="BESS_RTE_charge_efficiency"
                                            value={formData.BESS_RTE_charge_efficiency}
                                            onChange={handleInputChange}
                                            type="number"
                                            inputProps={{ step: 0.01 }}
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Discharging Efficiency */}
                                <Grid item xs={12} sm={1.79}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Discharging Efficiency
                                            <InfoTooltip sx={{ ml: "2%" }} text="Percentage of stored energy delivered during discharge." />
                                        </Typography>

                                        <TextField
                                            name="BESS_RTE_discharge_efficiency"
                                            value={formData.BESS_RTE_discharge_efficiency}
                                            onChange={handleInputChange}
                                            type="number"
                                            inputProps={{ step: 0.01 }}
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Minimum Capacity */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Minimum Capacity (kWh)</Typography>
                                        <TextField
                                            name="min_bess_kWh"
                                            value={formData.min_bess_kWh}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Capacity Step Size */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Capacity Step Size (kWh)</Typography>
                                        <TextField
                                            name="bess_step_size_kWh"
                                            value={formData.bess_step_size_kWh}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Minimum SOC */}
                                <Grid item xs={12} sm={1.79}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Minimum SOC (%)
                                            <InfoTooltip sx={{ ml: "2%" }} text="Minimum battery charge level allowed during operation." />
                                        </Typography>
                                        <TextField
                                            name="batt_min_SOC"
                                            value={formData.batt_min_SOC}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Maximum Capacity */}
                                <Grid item xs={12} sm={2.8} sx={{ display: "none" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Maximum Capacity (kWh)</Typography>
                                        <TextField
                                            name="max_bess_kWh"
                                            value={formData.max_bess_kWh}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Maximum SOC */}
                                <Grid item xs={12} sm={1.79}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Maximum SOC (%)
                                            <InfoTooltip sx={{ ml: "2%" }} text="Maximum battery charge level allowed during operation." />
                                        </Typography>
                                        <TextField
                                            name="batt_max_SOC"
                                            value={formData.batt_max_SOC}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* C Rating */}
                                <Grid item xs={12} sm={1.79}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>C Rating
                                            <InfoTooltip sx={{ ml: "2%" }} text="Rate at which the battery can be charged or discharged relative to its capacity." />
                                        </Typography>
                                        <TextField
                                            name="C_rating"
                                            value={formData.C_rating}
                                            onChange={handleInputChange}
                                            type="number"
                                            inputProps={{ step: 0.01 }}
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* BESS Brand */}
                                <Grid item xs={12} sm={1.79}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>BESS Brand / Model
                                            <InfoTooltip sx={{ ml: "2%" }} text="Upcoming features" />
                                        </Typography>
                                        <Select
                                            name="bess_brand_model"
                                            value="Customize"
                                            disabled
                                            fullWidth
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                "&.Mui-disabled": {
                                                    "& .MuiSelect-select": { WebkitTextFillColor: "rgba(0, 0, 0, 0.8)" }
                                                }
                                            }}
                                        >
                                            <MenuItem value="Customize">Customize</MenuItem>
                                        </Select>
                                    </Box>
                                </Grid>

                            </Grid>

                            {/* Costs & Capex*/}
                            <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>🏗️Costs & Capex</Typography>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "2%", width: "100%" }}>
                                {/* CAPEX */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>

                                        <Typography sx={{ fontSize: "1rem" }}>
                                            CAPEX incl. installation (RM per kWh){" "}
                                            <InfoTooltip sx={{ ml: "1%" }} text="Total installed cost per kWh of battery capacity, including installation." />
                                        </Typography>


                                        <TextField
                                            name="capex_rm_per_kWh"
                                            value={formData.capex_rm_per_kWh}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* OPEX */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>
                                            OPEX (% of CAPEX){" "}
                                            <InfoTooltip sx={{ ml: "1%" }} text="Annual operating cost as a percentage of the total installed CAPEX, for Operation & Maintenance, etc." />
                                        </Typography>
                                        <TextField
                                            name="operating_cost_percentage"
                                            value={formData.operating_cost_percentage}
                                            onChange={handleInputChange}
                                            type="number"
                                            inputProps={{ step: 0.1 }}
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Taming the BESS Button */}
                            <Box sx={{ position: 'relative', width: '100%' }}>
                                {/* Main Button with Pulse Animation */}
                                <Button
                                    variant="contained"
                                    onClick={handleTamingBESS}
                                    disabled={loading.bess}
                                    sx={{
                                        background: loading.bess
                                            ? 'linear-gradient(135deg, #9C27B0 0%, #7E4A9D 100%)'
                                            : 'linear-gradient(135deg, #7E4A9D 0%, #9C27B0 100%)',
                                        color: "#fff",
                                        borderRadius: "12px",
                                        textTransform: "none",
                                        px: 3,
                                        py: 1.5,
                                        mb: 2,
                                        width: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        animation: loading.bess ? 'pulse 2s infinite' : 'none',
                                        '&:hover': {
                                            transform: loading.bess ? 'none' : 'translateY(-2px)',
                                            boxShadow: loading.bess ? 'none' : '0 6px 20px rgba(126,74,157,0.4)',
                                            background: loading.bess
                                                ? 'linear-gradient(135deg, #9C27B0 0%, #7E4A9D 100%)'
                                                : 'linear-gradient(135deg, #6A3490 0%, #8E24AA 100%)'
                                        },
                                        '&.Mui-disabled': {
                                            background: '#E1BEE7',
                                            opacity: 0.9
                                        },
                                        '@keyframes pulse': {
                                            '0%': {
                                                boxShadow: '0 0 0 0 rgba(156, 39, 176, 0.7)'
                                            },
                                            '70%': {
                                                boxShadow: '0 0 0 10px rgba(156, 39, 176, 0)'
                                            },
                                            '100%': {
                                                boxShadow: '0 0 0 0 rgba(156, 39, 176, 0)'
                                            }
                                        }
                                    }}
                                >
                                    {loading.bess ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <CircularProgress size={20} sx={{ color: '#fff' }} />
                                                <Box sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                    {progressData?.state === 'PROGRESS' && `Processing ${animatedPercentage}%`}
                                                    {progressData?.state === 'PENDING' && 'Initializing...'}
                                                    {progressData?.state === 'SUCCESS' && 'Complete!'}
                                                </Box>
                                            </Box>
                                            {progressData?.state === 'PROGRESS' && (
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                    {animatedPercentage}%
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BoltIcon sx={{ fontSize: 20 }} />
                                            Taming the BESS
                                            <ArrowForwardIcon sx={{ fontSize: 20, ml: 1 }} />
                                        </Box>
                                    )}
                                </Button>

                                {/* Interactive Progress Section - Only shows when processing */}
                                {loading.bess && progressData?.percentage > 0 && (
                                    <Grow in={true} timeout={500}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #F3E5F5 0%, #EDE7F6 100%)',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                            onClick={() => setShowDetails(!showDetails)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SpeedIcon sx={{ color: '#7E4A9D', fontSize: 20 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#4A148C' }}>
                                                        Processing Status
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" sx={{ color: '#7E4A9D' }}>
                                                    {animatedPercentage}% Complete
                                                </Typography>
                                            </Box>

                                            <LinearProgress
                                                variant="determinate"
                                                value={animatedPercentage}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    backgroundColor: '#E1BEE7',
                                                    mb: 1.5,
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: '#7E4A9D',
                                                        borderRadius: 4,
                                                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        backgroundImage: 'linear-gradient(90deg, #7E4A9D 0%, #9C27B0 100%)'
                                                    }
                                                }}
                                            />

                                            {/* Animated Shine Effect */}
                                            <Box sx={{ position: 'relative', height: 4, overflow: 'hidden', borderRadius: 2 }}>
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    height: '100%',
                                                    width: `${animatedPercentage}%`,
                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                                                    animation: 'shine 2s infinite',
                                                    '@keyframes shine': {
                                                        '0%': { transform: 'translateX(-100%)' },
                                                        '100%': { transform: 'translateX(100%)' }
                                                    }
                                                }} />
                                            </Box>

                                            {/* Expandable Details */}
                                            {showDetails && (
                                                <Zoom in={showDetails} timeout={300}>
                                                    <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid rgba(126,74,157,0.2)' }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                            <Typography variant="caption" sx={{ color: '#6A3490' }}>
                                                                <TimelineIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                                Current Task:
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#4A148C', fontWeight: 500 }}>
                                                                {progressData.status || 'Processing data...'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="caption" sx={{ color: '#6A3490' }}>
                                                                Progress: {progressData.current || 0}/{progressData.total || 0}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#6A3490' }}>
                                                                ETA: ~{Math.ceil((100 - animatedPercentage) / 10)}s
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Zoom>
                                            )}

                                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: '#7E4A9D' }}>
                                                {showDetails ? '▼ Tap to hide details' : '▶ Tap to show details'}
                                            </Typography>
                                        </Paper>
                                    </Grow>
                                )}

                                {/* Success Animation */}
                                {progressData?.state === 'SUCCESS' && !loading.bess && (
                                    <Zoom in={true} timeout={500}>
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                                                textAlign: 'center',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                                <CheckCircleIcon sx={{ fontSize: 32, color: '#2E7D32', animation: 'bounce 0.5s ease' }} />
                                                <Typography variant="h6" sx={{ color: '#1B5E20', fontWeight: 600 }}>
                                                    Success!
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#2E7D32' }}>
                                                BESS has been successfully configured
                                            </Typography>

                                            {/* Animated success particles */}
                                            {/* {[...Array(3)].map((_, i) => (
                                                <Box
                                                    key={i}
                                                    sx={{
                                                        position: 'absolute',
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#4CAF50',
                                                        animation: `floatUp 1s ease-out ${i * 0.2}s`,
                                                        left: `${30 + i * 20}%`,
                                                        bottom: 0,
                                                        '@keyframes floatUp': {
                                                            '0%': {
                                                                transform: 'translateY(0)',
                                                                opacity: 1
                                                            },
                                                            '100%': {
                                                                transform: 'translateY(-40px)',
                                                                opacity: 0
                                                            }
                                                        }
                                                    }}
                                                />
                                            ))} */}
                                        </Paper>
                                    </Zoom>
                                )}
                            </Box>

                            {/* BESS Results */}
                            {progressData?.state === "SUCCESS" && (
                                <Box sx={{ mt: 3 }}>
                                    <Paper sx={{ p: 3, backgroundColor: "#e8f5e9", border: "1px solid #4caf50" }}>
                                        <Typography variant="h5" gutterBottom sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                                            ✅ BESS Analysis Results
                                        </Typography>

                                        {/* Graph 1: Yearly Savings */}
                                        <Box sx={{ width: '100%', mt: 3 }}>
                                            <Paper sx={{ p: 3, height: 500 }}>
                                                <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3 }}>
                                                    BESS Analysis: Yearly Savings vs System Size
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />

                                                        <XAxis
                                                            dataKey="bessSize"
                                                            label={{
                                                                value: 'BESS Size (kWh)',
                                                                position: 'bottom',
                                                                offset: 0,
                                                                style: { fontWeight: 'bold' }
                                                            }}
                                                            tick={{ fontSize: 12 }}
                                                        />

                                                        <YAxis
                                                            yAxisId="left"
                                                            label={{
                                                                value: 'Amount (RM)',
                                                                angle: -90,
                                                                position: 'insideLeft',
                                                                style: { fontWeight: 'bold' }
                                                            }}
                                                            tickFormatter={(value) => {
                                                                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                                                                if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                                                                return value;
                                                            }}
                                                            tick={{ fontSize: 11 }}
                                                            width={70}
                                                        />

                                                        <YAxis
                                                            yAxisId="right"
                                                            orientation="right"
                                                            label={{
                                                                value: 'MD Shaved (kW)',
                                                                angle: 90,
                                                                position: 'insideRight',
                                                                style: { fontWeight: 'bold' }
                                                            }}
                                                            tickFormatter={(value) => value.toFixed(1)}
                                                            tick={{ fontSize: 11 }}
                                                            width={70}
                                                        />

                                                        <Tooltip
                                                            formatter={(value, name) => [
                                                                typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value,
                                                                name
                                                            ]}
                                                            labelFormatter={(label) => `BESS Size: ${label} kWh`}
                                                        />

                                                        <Legend
                                                            verticalAlign="top"
                                                            height={36}
                                                            wrapperStyle={{ fontSize: '14px', fontWeight: 'bold' }}
                                                        />

                                                        <Line
                                                            yAxisId="left"
                                                            type="monotone"
                                                            dataKey="yearlySavings"
                                                            stroke="#ffc658"
                                                            name="Yearly Savings (RM)"
                                                            strokeWidth={2}
                                                            dot={{ r: 1, fill: "#ffc658" }}
                                                            activeDot={{ r: 8 }}
                                                        />

                                                        <Line
                                                            yAxisId="right"
                                                            type="monotone"
                                                            dataKey="mdShaved"
                                                            stroke="#8884d8"
                                                            name="MD Shaved (kW)"
                                                            strokeWidth={2}
                                                            dot={{ r: 1, fill: "#8884d8" }}
                                                            activeDot={{ r: 8 }}
                                                        />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Box>

                                        {/* Graph 2: ROI and Incremental Savings */}
                                        <Box sx={{ width: '100%', mt: 3 }}>
                                            <Paper sx={{ p: 3, height: 500 }}>
                                                <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3 }}>
                                                    BESS Analysis: ROI vs System Size
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />

                                                        <XAxis
                                                            dataKey="bessSize"
                                                            label={{
                                                                value: 'BESS Size (kWh)',
                                                                position: 'bottom',
                                                                offset: 0,
                                                                style: { fontWeight: 'bold' }
                                                            }}
                                                            tick={{ fontSize: 12 }}
                                                        />

                                                        <YAxis
                                                            yAxisId="left"
                                                            label={{
                                                                value: 'ROI (years)',
                                                                angle: -90,
                                                                position: 'insideLeft',
                                                                style: { fontWeight: 'bold' }
                                                            }}
                                                            tick={{ fontSize: 12 }}
                                                        />

                                                        <YAxis
                                                            yAxisId="right"
                                                            orientation="right"
                                                            label={{
                                                                value: 'Incremental Savings (RM / kWh)',
                                                                angle: 90,
                                                                position: 'insideRight',
                                                                style: { fontWeight: 'bold' }
                                                            }}
                                                            tick={{ fontSize: 12 }}
                                                        />

                                                        <Tooltip
                                                            formatter={(value, name) => {
                                                                if (name === "ROI (years)") return [value.toFixed(2), name];
                                                                return [value.toLocaleString(), name];
                                                            }}
                                                            labelFormatter={(label) => `BESS Size: ${label} kWh`}
                                                        />

                                                        <Legend
                                                            verticalAlign="top"
                                                            height={36}
                                                            wrapperStyle={{ fontSize: '14px', fontWeight: 'bold' }}
                                                        />

                                                        <Line
                                                            yAxisId="left"
                                                            type="monotone"
                                                            dataKey="roi"
                                                            stroke="#8884d8"
                                                            name="ROI (years)"
                                                            strokeWidth={2}
                                                            dot={{ r: 1, fill: "#8884d8" }}
                                                            activeDot={{ r: 8 }}
                                                        />

                                                        {/* <Line
                                                            yAxisId="right"
                                                            type="monotone"
                                                            dataKey="incSavingsPerKwh"
                                                            stroke="#ff7f7f"
                                                            name="Incremental Savings (RM / kWh)"
                                                            strokeWidth={2}
                                                            dot={{ r: 1, fill: "#ff7f7f" }}
                                                            activeDot={{ r: 8 }}
                                                        /> */}
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Box>

                                    </Paper>
                                </Box>
                            )}
                            {/* Error State */}
                            {progressData?.state === "FAILURE" && (
                                <Paper sx={{ p: 2, backgroundColor: "#ffebee", border: "1px solid #f44336" }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: "#c62828" }}>
                                        ❌ Task Failed
                                    </Typography>
                                    <Typography>{progressData.status || "Unknown error occurred"}</Typography>
                                </Paper>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* 3: Pick your BESS */}
                    <Accordion sx={{ "&::before": { display: "none" }, mt: "1%", borderRadius: "0.5rem", padding: "0.5%", border: "1px solid #d8d8d8" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontSize: "1rem" }}>3: Pick your BESS, enjoy eCOS in Action</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>🔋BESS Parameters</Typography>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "2%", width: "100%" }}>
                                {/* Chosen BESS Capacity */}
                                <Grid item xs={12} sm={3.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Chosen BESS Capacity (kWh)
                                            <InfoTooltip sx={{ ml: "2%" }} text="The selected battery energy capacity for the BESS system." />
                                        </Typography>
                                        <TextField
                                            name="chosen_bess_kWh"
                                            value={formData.chosen_bess_kWh}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Chosen System Life */}
                                <Grid item xs={12} sm={3.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>System Life (years)
                                            <InfoTooltip sx={{ ml: "2%" }} text="The selected system life for the BESS system." />
                                        </Typography>
                                        <TextField
                                            name="chosen_system_life"
                                            value={formData.chosen_system_life}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            error={formData.chosen_system_life > 25}
                                            helperText={formData.chosen_system_life > 25 ? "System life must be 25 years or less" : ""}
                                            inputProps={{ max: 25, min: 1 }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                                {/* Chosen SOH Y-O-Y degradation */}
                                <Grid item xs={12} sm={3.8}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Chosen SOH Y-O-Y degradation (%)
                                            <InfoTooltip sx={{ ml: "2%" }} text="The selected state of health year-over-year degradation for the BESS system." />
                                        </Typography>
                                        <TextField
                                            name="chosen_soh_degradation"
                                            value={formData.chosen_soh_degradation}
                                            onChange={handleInputChange}
                                            type="number"
                                            margin="dense"
                                            fullWidth
                                            error={formData.chosen_soh_degradation > 10}
                                            helperText={formData.chosen_soh_degradation > 10 ? "SOH degradation must be 10% or less" : ""}
                                            inputProps={{ max: 10, min: 0 }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>

                                {/* eCOS in Action Button */}
                                <Box>
                                    <Button
                                        variant="contained"
                                        endIcon={loading.ecos ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                                        onClick={handleECOS}
                                        disabled={loading.ecos || !file || hasValidationErrors}
                                        sx={{
                                            backgroundColor: "#7E4A9D",
                                            color: "#fff",
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            px: 3,
                                            py: 1,
                                            mb: 1,
                                            "&:hover": { backgroundColor: "#6A3490" },
                                            "&.Mui-disabled": { backgroundColor: "#ccc" }
                                        }}
                                    >
                                        {loading.ecos ? "Processing..." : "eCOS in Action"}
                                    </Button>
                                </Box>
                            </Grid>

                            {/* eCOS Results Display with Charts */}
                            {ecosResult && (
                                <Paper sx={{ p: 3, backgroundColor: "#e3f2fd", mt: 3, border: "1px solid #2196f3" }}>
                                    <Typography variant="h5" gutterBottom sx={{ color: "#1976d2", fontWeight: "bold" }}>
                                        ⚡ eCOS Operation Results
                                    </Typography>

                                    {/* Key Metrics Summary */}
                                    {/* Key Metrics Cards - Beautiful UI */}
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                sm: 'repeat(2, 1fr)',
                                                md: 'repeat(3, 1fr)',
                                                lg: 'repeat(5, 1fr)',
                                            },
                                            gap: 2,
                                            mb: 4,
                                        }}
                                    >
                                        <Paper sx={{ p: 2, minWidth: 0, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>ROI</Typography>
                                            <Typography sx={{ fontWeight: 'bold', my: 1, fontSize: '1.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {ecosResult.roi_yr?.toFixed(2)} <span style={{ fontSize: '0.9rem' }}>years</span>
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <Box component="span" sx={{ fontSize: '1.2rem' }}>⏱️</Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Return on Investment</Typography>
                                            </Box>
                                        </Paper>

                                        <Paper sx={{ p: 2, minWidth: 0, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', borderRadius: 2, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>CAPEX</Typography>
                                            <Typography sx={{ fontWeight: 'bold', my: 1, fontSize: 'clamp(1.1rem, 4vw, 1.7rem)', wordBreak: 'break-word', lineHeight: 1.2 }}>
                                                <span style={{ fontSize: '0.6em' }}>RM</span> {ecosResult.capex_rm?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <Box component="span" sx={{ fontSize: '1.2rem' }}>💰</Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Investment</Typography>
                                            </Box>
                                        </Paper>

                                        <Paper sx={{ p: 2, minWidth: 0, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', borderRadius: 2, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Max Demand Before</Typography>
                                            <Typography sx={{ fontWeight: 'bold', my: 1, fontSize: '1.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {ecosResult.MD_before_kW?.toLocaleString()} <span style={{ fontSize: '0.9rem' }}>kW</span>
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <Box component="span" sx={{ fontSize: '1.2rem' }}>📈</Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Peak Demand (Before)</Typography>
                                            </Box>
                                        </Paper>

                                        <Paper sx={{ p: 2, minWidth: 0, background: 'linear-gradient(135deg, #00a136 0%, #00c29e 100%)', color: 'white', borderRadius: 2, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Max Demand After</Typography>
                                            <Typography sx={{ fontWeight: 'bold', my: 1, fontSize: '1.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {ecosResult.MD_after_kW?.toLocaleString()} <span style={{ fontSize: '0.9rem' }}>kW</span>
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <Box component="span" sx={{ fontSize: '1.2rem' }}>📉</Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Peak Demand (After)</Typography>
                                            </Box>
                                        </Paper>

                                        <Paper sx={{ p: 2, minWidth: 0, background: 'linear-gradient(135deg, #fa709a 0%, #f3cf00 100%)', color: 'white', borderRadius: 2, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Monthly MD Savings</Typography>
                                            <Typography sx={{ fontWeight: 'bold', my: 1, fontSize: '1.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <span style={{ fontSize: '0.9rem' }}>RM</span> {ecosResult.MD_savings_rm?.toLocaleString()}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <Box component="span" sx={{ fontSize: '1.2rem' }}>💵</Box>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Per Month</Typography>
                                            </Box>
                                        </Paper>
                                    </Box>

                                    {/* Graph 1: Net Load Comparison */}
                                    <Paper sx={{ p: 2, height: 420, mb: 4 }}>
                                        <Typography
                                            variant="h6"
                                            align="center"
                                            sx={{ color: "#1976d2", fontWeight: 600, mb: 1 }}
                                        >
                                            Net Load Comparison: Before vs After BESS
                                        </Typography>

                                        <ResponsiveContainer width="100%" height="85%">
                                            <LineChart
                                                data={data}
                                                margin={{ top: 10, right: 30, left: 10, bottom: 50 }}
                                            >
                                                <defs>
                                                    {/* 🔥 Gradient for Peak Hours */}
                                                    <linearGradient id="peakGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#ff0000" stopOpacity={0.25} />
                                                        <stop offset="100%" stopColor="#ff8989" stopOpacity={0.25} />
                                                    </linearGradient>
                                                </defs>

                                                <CartesianGrid strokeDasharray="3 3" />

                                                {/* 🔶 Peak Hours */}
                                                <ReferenceArea
                                                    x1={peakStart}
                                                    x2={peakEnd}
                                                    fill="url(#peakGradient)"
                                                    ifOverflow="extendDomain"
                                                />

                                                <XAxis
                                                    dataKey="time"
                                                    height={60}
                                                    interval={3}
                                                    tick={{ fontSize: 11 }}
                                                />

                                                <YAxis
                                                    tick={{ fontSize: 11 }}
                                                    label={{
                                                        value: "Load (kW)",
                                                        angle: -90,
                                                        position: "insideLeft"
                                                    }}
                                                />

                                                <Tooltip content={<CustomTooltip />} />

                                                <Legend wrapperStyle={{ fontSize: 12 }} />

                                                {/* 🟡 Before */}
                                                <Line
                                                    type="monotone"
                                                    dataKey="netLoadBefore"
                                                    stroke="#fdd835"
                                                    name="Before BESS"
                                                    strokeWidth={2.5}
                                                    dot={false}
                                                    activeDot={{ r: 5 }}
                                                />

                                                {/* 🟢 After */}
                                                <Line
                                                    type="monotone"
                                                    dataKey="netLoadAfter"
                                                    stroke="#43a047"
                                                    name="After BESS"
                                                    strokeWidth={2.5}
                                                    dot={false}
                                                    activeDot={{ r: 5 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Paper>

                                    {/* Graph 2: Battery Power and SOC */}
                                    <Paper sx={{ p: 2, height: 450 }}>
                                        <Typography variant="h6" gutterBottom align="center" sx={{ color: "#1976d2" }}>
                                            Battery Performance: Power Output and State of Charge
                                        </Typography>

                                        <ResponsiveContainer width="100%" height="85%">
                                            <ComposedChart
                                                data={prepareEcosChartData(ecosResult)}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="time"
                                                    height={80}
                                                    interval={1}
                                                    tick={{ fontSize: 11 }}
                                                    label={{
                                                        value: 'Time (30-min intervals)',
                                                        position: 'bottom',
                                                        offset: 60
                                                    }}
                                                />

                                                <YAxis
                                                    yAxisId="left"
                                                    label={{ value: 'Battery Power (kW)', angle: -90, position: 'left' }}
                                                    tickFormatter={(value) => value.toFixed(0)}
                                                />

                                                <YAxis
                                                    yAxisId="right"
                                                    orientation="right"
                                                    label={{ value: 'State of Charge (%)', angle: 90, position: 'right' }}
                                                    domain={[0, 100]}
                                                    tickFormatter={(value) => value.toFixed(0)}
                                                />

                                                <Tooltip content={<CustomTooltipp />} />
                                                <Legend
                                                    verticalAlign="top"
                                                    height={36}
                                                    payload={[
                                                        { value: 'Battery Charging', type: 'square', color: '#4caf50' },
                                                        { value: 'Battery Discharging', type: 'square', color: '#ff5252' },
                                                        { value: 'Battery SOC', type: 'line', color: '#8884d8' },
                                                    ]}
                                                />

                                                <Bar
                                                    yAxisId="left"
                                                    dataKey="battPower"
                                                    name="Battery Power"
                                                    opacity={0.7}
                                                >
                                                    {prepareEcosChartData(ecosResult).map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={entry.battPower >= 0 ? "#4caf50" : "#ff5252"}
                                                        />
                                                    ))}
                                                </Bar>

                                                <Line
                                                    yAxisId="right"
                                                    type="monotone"
                                                    dataKey="batterySOC"
                                                    stroke="#8884d8"
                                                    name="Battery SOC"
                                                    strokeWidth={3}
                                                    dot={false}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>

                                        {/* Legend for power colors */}
                                        {/* <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                                        <Typography sx={{ color: '#4caf50' }}>● Charging (Power {'>'} 0)</Typography>
                                        <Typography sx={{ color: '#ff5252' }}>● Discharging (Power {'<'} 0)</Typography>
                                        <Typography sx={{ color: '#8884d8' }}>● State of Charge (%)</Typography>
                                    </Box>
                                </Box> */}
                                    </Paper>


                                </Paper>
                            )}

                            {/* SOH Results Display */}
                            {sohResult && Array.isArray(sohResult) && (
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 3,
                                        background: 'linear-gradient(145deg, #f5f0ff 0%, #ede7f6 100%)',
                                        mt: 3,
                                        border: "1px solid #b39ddb",
                                        borderRadius: 3,
                                        boxShadow: '0 8px 32px rgba(103, 58, 183, 0.15)'
                                    }}
                                >
                                    {/* Header with Title and Export Button */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 3,
                                        pb: 2,
                                        borderBottom: '2px solid #9575cd'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                backgroundColor: '#7e4a9d',
                                                borderRadius: '50%',
                                                width: 44,
                                                height: 44,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.3)'
                                            }}>
                                                <span style={{ fontSize: '24px' }}>📉</span>
                                            </Box>
                                            <Box>
                                                <Typography variant="h5" sx={{ color: "#5e35b1", fontWeight: "bold" }}>
                                                    SOH Degradation Analysis
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "#9575cd" }}>
                                                    {sohResult.length} Years of Battery Performance Data
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* <Button
                                            variant="contained"
                                            size="medium"
                                            startIcon={<DownloadIcon />}

                                            sx={{
                                                backgroundColor: "#7e4a9d",
                                                color: "white",
                                                textTransform: "none",
                                                borderRadius: "8px",
                                                px: 3,
                                                py: 1,
                                                fontWeight: 500,
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: "#6a3d8a",
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 16px rgba(126, 74, 157, 0.4)'
                                                },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Download CSV Template
                                        </Button> */}
                                    </Box>

                                    {/* Summary Cards */}
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #7e4a9d',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Initial Usable Energy</Typography>
                                                <Typography variant="h5" sx={{ color: '#7e4a9d', fontWeight: 'bold' }}>
                                                    {sohResult[0]?.usable_energy_at_POC_kWh?.toFixed(2)} <span style={{ fontSize: '0.9rem', color: '#9575cd' }}>kWh</span>
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #b39ddb',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Final Usable Energy</Typography>
                                                <Typography variant="h5" sx={{ color: '#b39ddb', fontWeight: 'bold' }}>
                                                    {sohResult[sohResult.length - 1]?.usable_energy_at_POC_kWh?.toFixed(2)} <span style={{ fontSize: '0.9rem', color: '#9575cd' }}>kWh</span>
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #5e35b1',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Total Degradation</Typography>
                                                <Typography variant="h5" sx={{ color: '#5e35b1', fontWeight: 'bold' }}>
                                                    {((sohResult[0]?.usable_energy_at_POC_kWh - sohResult[sohResult.length - 1]?.usable_energy_at_POC_kWh) / sohResult[0]?.usable_energy_at_POC_kWh * 100).toFixed(1)}%
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #9575cd',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>MD Reduction</Typography>
                                                <Typography variant="h5" sx={{ color: '#9575cd', fontWeight: 'bold' }}>
                                                    {sohResult[0]?.MD_before_kW - sohResult[0]?.MD_after_kW} <span style={{ fontSize: '0.9rem', color: '#9575cd' }}>kW</span>
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* Three Charts in Cards */}
                                    <Typography variant="h6" sx={{ color: "#5e35b1", mb: 2, fontWeight: 'bold' }}>
                                        Performance Trends
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {/* Chart 1: Usable Energy */}
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={{
                                                p: 2,
                                                height: 380,
                                                borderRadius: 3,
                                                background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                                border: '1px solid #d1c4e9',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 32px rgba(126, 74, 157, 0.25)'
                                                }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#5e35b1', borderRadius: '50%' }} />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                                                        Usable Energy Degradation
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, color: '#9575cd' }}>
                                                    Battery capacity loss over time
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    {(() => {
                                                        const energyData = prepareSOHChartData(sohResult);
                                                        const minEnergy = Math.min(...energyData.map(d => d.usableEnergy));
                                                        const maxEnergy = Math.max(...energyData.map(d => d.usableEnergy));
                                                        const padding = (maxEnergy - minEnergy) * 0.1;

                                                        return (
                                                            <LineChart data={energyData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e1d7f0" />
                                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <YAxis domain={[0, maxEnergy + padding]} tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        borderRadius: 8,
                                                                        border: '1px solid #5e35b1',
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                                        boxShadow: '0 4px 12px rgba(126, 74, 157, 0.2)'
                                                                    }}
                                                                    formatter={(value) => [`${value} kWh`, 'Usable Energy']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="usableEnergy"
                                                                    stroke="#5e35b1"
                                                                    strokeWidth={3}
                                                                    dot={{ r: 6, fill: "#5e35b1", strokeWidth: 2, stroke: '#fff' }}
                                                                    activeDot={{ r: 8, fill: "#5e35b1" }}
                                                                />
                                                            </LineChart>
                                                        );
                                                    })()}
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Grid>

                                        {/* Chart 2: Monthly MD Savings */}
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={{
                                                p: 2,
                                                height: 380,
                                                borderRadius: 3,
                                                background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                                border: '1px solid #d1c4e9',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 32px rgba(126, 74, 157, 0.25)'
                                                }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#5e35b1', borderRadius: '50%' }} />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                                                        Monthly MD Savings
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, color: '#9575cd' }}>
                                                    Consistent savings throughout battery life
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    {(() => {
                                                        const savingsData = prepareSOHChartData(sohResult);
                                                        const minSavings = Math.min(...savingsData.map(d => d.monthlyMDSavings));
                                                        const maxSavings = Math.max(...savingsData.map(d => d.monthlyMDSavings));
                                                        const padding = (maxSavings - minSavings) * 0.1;

                                                        return (
                                                            <LineChart data={savingsData} margin={{ top: 10, right: 20, left: 20, bottom: 30 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e1d7f0" />
                                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <YAxis
                                                                    domain={[minSavings - padding, maxSavings + padding]}
                                                                    tickFormatter={(value) => `RM ${(value / 1000).toFixed(1)}k`}
                                                                    tick={{ fontSize: 12, fill: '#5e35b1' }}
                                                                />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        borderRadius: 8,
                                                                        border: '1px solid #5e35b1',
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                                        boxShadow: '0 4px 12px rgba(126, 74, 157, 0.2)'
                                                                    }}
                                                                    formatter={(value) => [`RM ${Number(value).toLocaleString()}`, 'Monthly MD Savings']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="monthlyMDSavings"
                                                                    stroke="#5e35b1"
                                                                    strokeWidth={3}
                                                                    dot={{ r: 6, fill: "#5e35b1", strokeWidth: 2, stroke: '#fff' }}
                                                                    activeDot={{ r: 8, fill: "#5e35b1" }}
                                                                />
                                                            </LineChart>
                                                        );
                                                    })()}
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Grid>

                                        {/* Chart 3: MD Shaved */}
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={{
                                                p: 2,
                                                height: 380,
                                                borderRadius: 3,
                                                background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                                border: '1px solid #d1c4e9',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 32px rgba(126, 74, 157, 0.25)'
                                                }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#5e35b1', borderRadius: '50%' }} />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                                                        MD Shaved
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, color: '#9575cd' }}>
                                                    Peak demand reduction
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    {(() => {
                                                        const mdData = prepareSOHChartData(sohResult);
                                                        const minMd = Math.min(...mdData.map(d => d.mdShaved));
                                                        const maxMd = Math.max(...mdData.map(d => d.mdShaved));
                                                        const padding = (maxMd - minMd) * 0.1;

                                                        return (
                                                            <LineChart data={mdData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e1d7f0" />
                                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <YAxis domain={[minMd - padding, maxMd + padding]} tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        borderRadius: 8,
                                                                        border: '1px solid #5e35b1',
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                                        boxShadow: '0 4px 12px rgba(126, 74, 157, 0.2)'
                                                                    }}
                                                                    formatter={(value) => [`${value} kW`, 'MD Shaved']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="mdShaved"
                                                                    stroke="#5e35b1"
                                                                    strokeWidth={3}
                                                                    dot={{ r: 6, fill: "#5e35b1", strokeWidth: 2, stroke: '#fff' }}
                                                                    activeDot={{ r: 8, fill: "#7e4a9d" }}
                                                                />
                                                            </LineChart>
                                                        );
                                                    })()}
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* Data Table Section */}
                                    <Paper sx={{
                                        p: 2,
                                        mt: 4,
                                        borderRadius: 3,
                                        background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                        border: '1px solid #d1c4e9',
                                        boxShadow: '0 4px 16px rgba(126, 74, 157, 0.1)'
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="h6" sx={{ color: "#5e35b1", fontWeight: 'bold' }}>
                                                    Year-over-Year Data
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#9575cd' }}>
                                                    Detailed breakdown by year
                                                </Typography>
                                            </Box>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                onClick={exportSOHToExcel}
                                                sx={{
                                                    borderColor: "#7e4a9d",
                                                    color: "#7e4a9d",
                                                    '&:hover': {
                                                        borderColor: "#5e35b1",
                                                        backgroundColor: "#f3e5f5"
                                                    }
                                                }}
                                            >
                                                Export Table
                                            </Button>
                                        </Box>

                                        <Box sx={{
                                            maxHeight: 300,
                                            overflow: 'auto',
                                            borderRadius: 2,
                                            border: '1px solid #d1c4e9',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                                height: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f3e5f5',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#b39ddb',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                backgroundColor: '#7e4a9d',
                                            }
                                        }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{
                                                        background: 'linear-gradient(90deg, #7e4a9d 0%, #9575cd 100%)',
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 1
                                                    }}>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Year</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Usable Energy (kWh)</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Monthly MD Savings (RM)</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>MD Shaved (kW)</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Degradation (%)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {prepareSOHChartData(sohResult).map((row, index) => {
                                                        const prevEnergy = index > 0 ? prepareSOHChartData(sohResult)[index - 1].usableEnergy : row.usableEnergy;
                                                        const degradation = ((prevEnergy - row.usableEnergy) / prevEnergy * 100).toFixed(2);

                                                        return (
                                                            <tr
                                                                key={row.year}
                                                                style={{
                                                                    borderBottom: '1px solid #e1d7f0',
                                                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#faf5ff',
                                                                    transition: 'background-color 0.2s',
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3e5f5'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#faf5ff'}
                                                            >
                                                                <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#5e35b1' }}>Year {row.year}</td>
                                                                <td style={tableCellStyle}>{row.usableEnergy} kWh</td>
                                                                <td style={tableCellStyle}>RM {Number(row.monthlyMDSavings).toLocaleString()}</td>
                                                                <td style={tableCellStyle}>{row.mdShaved} kW</td>
                                                                <td style={{
                                                                    ...tableCellStyle,
                                                                    color: index > 0 ? '#d32f2f' : '#7e4a9d',
                                                                    fontWeight: index > 0 ? 'bold' : 'normal'
                                                                }}>
                                                                    {index > 0 ? `↓ ${degradation}%` : '-'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>

                                        {/* Footer Note */}
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Typography variant="caption" sx={{ color: '#b39ddb' }}>
                                                * Data shows degradation over {sohResult.length} year period
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Paper>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* 4: SOH Degradation Modelling */}
                    <Accordion sx={{ "&::before": { display: "none" }, mt: "1%", borderRadius: "0.5rem", padding: "0.5%", border: "1px solid #d8d8d8", display: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontSize: "1rem" }}>4: SOH Degradation Modelling</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "2%", width: "100%" }}>
                                <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>📉SOH Degradation Template</Typography>
                            </Grid>

                            {/* <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSOHTemplate}
                                sx={{
                                    backgroundColor: "#7E4A9D",
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    height: "40px",
                                    width: "20%",
                                    fontWeight: 500,
                                    "&:hover": { backgroundColor: "#6A3490" }
                                }}
                            >
                                Download SOH Template CSV
                            </Button> */}

                            {/* SOH CSV Upload */}
                            {/* <Box sx={{ border: "1px dashed #ccc", borderRadius: 2, p: 3, backgroundColor: "#fafafa" }}>
                                <Typography sx={{ fontWeight: "bold", mb: 1 }}>Upload your SOH CSV here</Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                        p: 3,
                                        backgroundColor: "#fff",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                    }}
                                    onClick={handleSohBrowseClick}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 40, color: "#999" }} />
                                    <Typography color="textSecondary">Drag and drop file here</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Limit 200MB per file • CSV, Excel
                                    </Typography>
                                    <Button variant="contained" sx={{ mt: 2 }} onClick={handleSohBrowseClick}>
                                        Browse files
                                    </Button>
                                    <input
                                        type="file"
                                        accept=".csv,.xlsx,.xls"
                                        ref={sohFileInputRef}
                                        style={{ display: "none" }}
                                        onChange={handleSohFileChange}
                                    />
                                </Box>
                                {sohError && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {sohError}
                                    </Typography>
                                )}
                                {sohFile && (
                                    <Typography color="primary" sx={{ mt: 1 }}>
                                        SOH CSV: {sohFile.name}
                                    </Typography>
                                )}
                            </Box> */}

                            {/* Run SOH Button */}
                            {/* <Box>
                                <Button
                                    variant="contained"
                                    endIcon={loading.soh ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                                    onClick={handleSOH}
                                    disabled={loading.soh || !file || !sohFile}
                                    sx={{
                                        backgroundColor: "#7E4A9D",
                                        color: "#fff",
                                        borderRadius: "10px",
                                        textTransform: "none",
                                        px: 3,
                                        py: 1,
                                        mb: 1,
                                        "&:hover": { backgroundColor: "#6A3490" },
                                        "&.Mui-disabled": { backgroundColor: "#ccc" }
                                    }}
                                >
                                    {loading.soh ? "Processing..." : "Run SOH Degradation"}
                                </Button>
                            </Box> */}


                            {/* SOH Results Display */}
                            {sohResult && Array.isArray(sohResult) && (
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 3,
                                        background: 'linear-gradient(145deg, #f5f0ff 0%, #ede7f6 100%)',
                                        mt: 3,
                                        border: "1px solid #b39ddb",
                                        borderRadius: 3,
                                        boxShadow: '0 8px 32px rgba(103, 58, 183, 0.15)'
                                    }}
                                >
                                    {/* Header with Title and Export Button */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 3,
                                        pb: 2,
                                        borderBottom: '2px solid #9575cd'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                backgroundColor: '#7e4a9d',
                                                borderRadius: '50%',
                                                width: 44,
                                                height: 44,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.3)'
                                            }}>
                                                <span style={{ fontSize: '24px' }}>📉</span>
                                            </Box>
                                            <Box>
                                                <Typography variant="h5" sx={{ color: "#5e35b1", fontWeight: "bold" }}>
                                                    SOH Degradation Analysis
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "#9575cd" }}>
                                                    {sohResult.length} Years of Battery Performance Data
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            size="medium"
                                            startIcon={<DownloadIcon />}

                                            sx={{
                                                backgroundColor: "#7e4a9d",
                                                color: "white",
                                                textTransform: "none",
                                                borderRadius: "8px",
                                                px: 3,
                                                py: 1,
                                                fontWeight: 500,
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: "#6a3d8a",
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 16px rgba(126, 74, 157, 0.4)'
                                                },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Download CSV Template
                                        </Button>
                                    </Box>

                                    {/* Summary Cards */}
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #7e4a9d',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Initial Usable Energy</Typography>
                                                <Typography variant="h5" sx={{ color: '#7e4a9d', fontWeight: 'bold' }}>
                                                    {sohResult[0]?.usable_energy_at_POC_kWh?.toFixed(2)} <span style={{ fontSize: '0.9rem', color: '#9575cd' }}>kWh</span>
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #b39ddb',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Final Usable Energy</Typography>
                                                <Typography variant="h5" sx={{ color: '#b39ddb', fontWeight: 'bold' }}>
                                                    {sohResult[sohResult.length - 1]?.usable_energy_at_POC_kWh?.toFixed(2)} <span style={{ fontSize: '0.9rem', color: '#9575cd' }}>kWh</span>
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #5e35b1',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>Total Degradation</Typography>
                                                <Typography variant="h5" sx={{ color: '#5e35b1', fontWeight: 'bold' }}>
                                                    {((sohResult[0]?.usable_energy_at_POC_kWh - sohResult[sohResult.length - 1]?.usable_energy_at_POC_kWh) / sohResult[0]?.usable_energy_at_POC_kWh * 100).toFixed(1)}%
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Paper sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: '#ffffff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #9575cd',
                                                boxShadow: '0 4px 12px rgba(126, 74, 157, 0.1)',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(126, 74, 157, 0.2)'
                                                }
                                            }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>MD Reduction</Typography>
                                                <Typography variant="h5" sx={{ color: '#9575cd', fontWeight: 'bold' }}>
                                                    {sohResult[0]?.MD_before_kW - sohResult[0]?.MD_after_kW} <span style={{ fontSize: '0.9rem', color: '#9575cd' }}>kW</span>
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* Three Charts in Cards */}
                                    <Typography variant="h6" sx={{ color: "#5e35b1", mb: 2, fontWeight: 'bold' }}>
                                        Performance Trends
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {/* Chart 1: Usable Energy */}
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={{
                                                p: 2,
                                                height: 380,
                                                borderRadius: 3,
                                                background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                                border: '1px solid #d1c4e9',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 32px rgba(126, 74, 157, 0.25)'
                                                }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#5e35b1', borderRadius: '50%' }} />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                                                        Usable Energy Degradation
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, color: '#9575cd' }}>
                                                    Battery capacity loss over time
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    {(() => {
                                                        const energyData = prepareSOHChartData(sohResult);
                                                        const minEnergy = Math.min(...energyData.map(d => d.usableEnergy));
                                                        const maxEnergy = Math.max(...energyData.map(d => d.usableEnergy));
                                                        const padding = (maxEnergy - minEnergy) * 0.1;

                                                        return (
                                                            <LineChart data={energyData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e1d7f0" />
                                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <YAxis domain={[0, maxEnergy + padding]} tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        borderRadius: 8,
                                                                        border: '1px solid #5e35b1',
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                                        boxShadow: '0 4px 12px rgba(126, 74, 157, 0.2)'
                                                                    }}
                                                                    formatter={(value) => [`${value} kWh`, 'Usable Energy']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="usableEnergy"
                                                                    stroke="#5e35b1"
                                                                    strokeWidth={3}
                                                                    dot={{ r: 6, fill: "#5e35b1", strokeWidth: 2, stroke: '#fff' }}
                                                                    activeDot={{ r: 8, fill: "#5e35b1" }}
                                                                />
                                                            </LineChart>
                                                        );
                                                    })()}
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Grid>

                                        {/* Chart 2: Monthly MD Savings */}
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={{
                                                p: 2,
                                                height: 380,
                                                borderRadius: 3,
                                                background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                                border: '1px solid #d1c4e9',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 32px rgba(126, 74, 157, 0.25)'
                                                }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#5e35b1', borderRadius: '50%' }} />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                                                        Monthly MD Savings
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, color: '#9575cd' }}>
                                                    Consistent savings throughout battery life
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    {(() => {
                                                        const savingsData = prepareSOHChartData(sohResult);
                                                        const minSavings = Math.min(...savingsData.map(d => d.monthlyMDSavings));
                                                        const maxSavings = Math.max(...savingsData.map(d => d.monthlyMDSavings));
                                                        const padding = (maxSavings - minSavings) * 0.1;

                                                        return (
                                                            <LineChart data={savingsData} margin={{ top: 10, right: 20, left: 20, bottom: 30 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e1d7f0" />
                                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <YAxis
                                                                    domain={[minSavings - padding, maxSavings + padding]}
                                                                    tickFormatter={(value) => `RM ${(value / 1000).toFixed(1)}k`}
                                                                    tick={{ fontSize: 12, fill: '#5e35b1' }}
                                                                />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        borderRadius: 8,
                                                                        border: '1px solid #5e35b1',
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                                        boxShadow: '0 4px 12px rgba(126, 74, 157, 0.2)'
                                                                    }}
                                                                    formatter={(value) => [`RM ${Number(value).toLocaleString()}`, 'Monthly MD Savings']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="monthlyMDSavings"
                                                                    stroke="#5e35b1"
                                                                    strokeWidth={3}
                                                                    dot={{ r: 6, fill: "#5e35b1", strokeWidth: 2, stroke: '#fff' }}
                                                                    activeDot={{ r: 8, fill: "#5e35b1" }}
                                                                />
                                                            </LineChart>
                                                        );
                                                    })()}
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Grid>

                                        {/* Chart 3: MD Shaved */}
                                        <Grid item xs={12} md={4}>
                                            <Paper sx={{
                                                p: 2,
                                                height: 380,
                                                borderRadius: 3,
                                                background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                                border: '1px solid #d1c4e9',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 32px rgba(126, 74, 157, 0.25)'
                                                }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#5e35b1', borderRadius: '50%' }} />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                                                        MD Shaved
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, color: '#9575cd' }}>
                                                    Peak demand reduction
                                                </Typography>

                                                <ResponsiveContainer width="100%" height="85%">
                                                    {(() => {
                                                        const mdData = prepareSOHChartData(sohResult);
                                                        const minMd = Math.min(...mdData.map(d => d.mdShaved));
                                                        const maxMd = Math.max(...mdData.map(d => d.mdShaved));
                                                        const padding = (maxMd - minMd) * 0.1;

                                                        return (
                                                            <LineChart data={mdData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="#e1d7f0" />
                                                                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <YAxis domain={[minMd - padding, maxMd + padding]} tick={{ fontSize: 12, fill: '#5e35b1' }} />
                                                                <Tooltip
                                                                    contentStyle={{
                                                                        borderRadius: 8,
                                                                        border: '1px solid #5e35b1',
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                                        boxShadow: '0 4px 12px rgba(126, 74, 157, 0.2)'
                                                                    }}
                                                                    formatter={(value) => [`${value} kW`, 'MD Shaved']}
                                                                />
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="mdShaved"
                                                                    stroke="#5e35b1"
                                                                    strokeWidth={3}
                                                                    dot={{ r: 6, fill: "#5e35b1", strokeWidth: 2, stroke: '#fff' }}
                                                                    activeDot={{ r: 8, fill: "#7e4a9d" }}
                                                                />
                                                            </LineChart>
                                                        );
                                                    })()}
                                                </ResponsiveContainer>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* Data Table Section */}
                                    <Paper sx={{
                                        p: 2,
                                        mt: 4,
                                        borderRadius: 3,
                                        background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                                        border: '1px solid #d1c4e9',
                                        boxShadow: '0 4px 16px rgba(126, 74, 157, 0.1)'
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="h6" sx={{ color: "#5e35b1", fontWeight: 'bold' }}>
                                                    Year-over-Year Data
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#9575cd' }}>
                                                    Detailed breakdown by year
                                                </Typography>
                                            </Box>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                onClick={exportSOHToExcel}
                                                sx={{
                                                    borderColor: "#7e4a9d",
                                                    color: "#7e4a9d",
                                                    '&:hover': {
                                                        borderColor: "#5e35b1",
                                                        backgroundColor: "#f3e5f5"
                                                    }
                                                }}
                                            >
                                                Export Table
                                            </Button>
                                        </Box>

                                        <Box sx={{
                                            maxHeight: 300,
                                            overflow: 'auto',
                                            borderRadius: 2,
                                            border: '1px solid #d1c4e9',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                                height: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f3e5f5',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#b39ddb',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                backgroundColor: '#7e4a9d',
                                            }
                                        }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{
                                                        background: 'linear-gradient(90deg, #7e4a9d 0%, #9575cd 100%)',
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 1
                                                    }}>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Year</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Usable Energy (kWh)</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Monthly MD Savings (RM)</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>MD Shaved (kW)</th>
                                                        <th style={{ ...tableHeaderStyle, color: 'black', fontWeight: 600 }}>Degradation (%)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {prepareSOHChartData(sohResult).map((row, index) => {
                                                        const prevEnergy = index > 0 ? prepareSOHChartData(sohResult)[index - 1].usableEnergy : row.usableEnergy;
                                                        const degradation = ((prevEnergy - row.usableEnergy) / prevEnergy * 100).toFixed(2);

                                                        return (
                                                            <tr
                                                                key={row.year}
                                                                style={{
                                                                    borderBottom: '1px solid #e1d7f0',
                                                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#faf5ff',
                                                                    transition: 'background-color 0.2s',
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3e5f5'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#faf5ff'}
                                                            >
                                                                <td style={{ ...tableCellStyle, fontWeight: 'bold', color: '#5e35b1' }}>Year {row.year}</td>
                                                                <td style={tableCellStyle}>{row.usableEnergy} kWh</td>
                                                                <td style={tableCellStyle}>RM {Number(row.monthlyMDSavings).toLocaleString()}</td>
                                                                <td style={tableCellStyle}>{row.mdShaved} kW</td>
                                                                <td style={{
                                                                    ...tableCellStyle,
                                                                    color: index > 0 ? '#d32f2f' : '#7e4a9d',
                                                                    fontWeight: index > 0 ? 'bold' : 'normal'
                                                                }}>
                                                                    {index > 0 ? `↓ ${degradation}%` : '-'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>

                                        {/* Footer Note */}
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Typography variant="caption" sx={{ color: '#b39ddb' }}>
                                                * Data shows degradation over {sohResult.length} year period
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Paper>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
            {tabValue === 1 && (
                <Box>
                    {/* Section 1: Download CSV Template */}
                    <Accordion
                        defaultExpanded
                        sx={{
                            "&::before": { display: "none" },
                            borderRadius: "0.5rem",
                            padding: "0.5%",
                            border: "1px solid #d1c4e9",
                            mb: "1%"
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DownloadIcon sx={{ color: "#7e4a9d" }} />
                                <Typography sx={{ fontSize: "1rem", fontWeight: "bold", color: "#5e35b1" }}>
                                    1: Download CSV Template
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Grid container sx={{ rowGap: "2vh", columnGap: "2%", width: "100%" }}>
                                {/* Client Name */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Client Name :</Typography>
                                        <TextField
                                            name="client_name"
                                            value={formData.client_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter client name"
                                            margin="dense"
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" }
                                                }
                                            }}
                                            InputProps={{
                                                style: {
                                                    backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                    borderRadius: "1rem"
                                                }
                                            }}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>

                                {/* Current Tariff */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Current Tariff :</Typography>
                                        <Select
                                            name="tariff_before_bess"
                                            value={formData.tariff_before_bess}
                                            onChange={handleInputChange}
                                            fullWidth
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" }
                                            }}
                                        >
                                            <MenuItem value="MV-TOU">MV-TOU</MenuItem>
                                            <MenuItem value="Water & Sewerage Operator MV-TOU">Water & Sewerage Operator MV-TOU</MenuItem>
                                            <MenuItem value="MV-General">MV-General</MenuItem>
                                            <MenuItem value="HV-TOU">HV-TOU</MenuItem>
                                        </Select>
                                    </Box>
                                </Grid>

                                {/* Start Time */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Start Time :</Typography>
                                        <Select
                                            name="start_time"
                                            value={formData.start_time}
                                            onChange={handleInputChange}
                                            fullWidth
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 300, // limits dropdown height
                                                        borderRadius: 2,
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                                    },
                                                },
                                            }}
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" }
                                            }}
                                        >
                                            {Array.from({ length: 48 }).map((_, index) => {
                                                const hours = Math.floor(index / 2).toString().padStart(2, "0");
                                                const minutes = index % 2 === 0 ? "00" : "30";
                                                const value = `${hours}:${minutes}`;
                                                return <MenuItem key={value} value={value}>{value}</MenuItem>;
                                            })}
                                        </Select>
                                    </Box>
                                </Grid>

                                {/* End Time */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1%" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>End Time :</Typography>
                                        <Select
                                            name="end_time"
                                            value={formData.end_time}
                                            onChange={handleInputChange}
                                            fullWidth
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 300, // limit dropdown height
                                                        borderRadius: 2,
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                                    },
                                                },
                                            }}
                                            sx={{
                                                backgroundColor: "rgba(219, 210, 223, 0.45)",
                                                mt: "1%",
                                                borderRadius: "1rem",
                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" }
                                            }}
                                        >
                                            {Array.from({ length: 48 }).map((_, index) => {
                                                const hours = Math.floor(index / 2).toString().padStart(2, "0");
                                                const minutes = index % 2 === 0 ? "00" : "30";
                                                const value = `${hours}:${minutes}`;

                                                // Disable times earlier than start_time
                                                const isDisabled = formData.start_time && timeToMinutes(value) <= timeToMinutes(formData.start_time);

                                                return (
                                                    <MenuItem key={value} value={value} disabled={isDisabled}>
                                                        {value}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </Box>
                                </Grid>

                                {/* Include Weekends */}
                                <Grid item xs={12} sm={5.85}>
                                    <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem", alignItems: "center" }}>
                                        <Typography sx={{ fontSize: "1rem" }}>Include Weekends:</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.include_weekends === "true"}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            include_weekends: e.target.checked.toString(),
                                                        }))
                                                    }
                                                    sx={{

                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#4f3186e8', // track color when checked
                                                        },
                                                        '& .MuiSwitch-track': {
                                                            backgroundColor: '#d1c4e9', // track color when unchecked
                                                        },

                                                        '& .MuiSwitch-thumb': {
                                                            backgroundColor: '#7e4a9d', // track color when unchecked
                                                        },
                                                    }}
                                                />
                                            }
                                            label={formData.include_weekends === "true" ? "Yes" : "No"}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={downloadTemplate}
                                sx={{
                                    backgroundColor: "#7e4a9d",
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    height: "40px",
                                    fontWeight: 500,
                                    width: "auto",
                                    alignSelf: "flex-start",
                                    "&:hover": { backgroundColor: "#6a3d8a" }
                                }}
                            >
                                Download CSV Template
                            </Button>
                        </AccordionDetails>
                    </Accordion>

                    {/* Section 2: Upload CSV and Plot Data */}
                    <Accordion
                        defaultExpanded
                        sx={{
                            "&::before": { display: "none" },
                            borderRadius: "0.5rem",
                            padding: "0.5%",
                            border: "1px solid #d1c4e9"
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ShowChartIcon sx={{ color: "#7e4a9d" }} />
                                <Typography sx={{ fontSize: "1rem", fontWeight: "bold", color: "#5e35b1" }}>
                                    2: Upload CSV and Plot Data
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {/* CSV Upload */}
                            <Box sx={{ border: "1px dashed #b39ddb", borderRadius: 2, p: 3, backgroundColor: "#faf5ff" }}>
                                <Typography sx={{ fontWeight: "bold", mb: 1, color: "#5e35b1" }}>
                                    Upload your CSV file
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                        p: 3,
                                        backgroundColor: "#fff",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        border: "1px solid #d1c4e9",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            borderColor: "#7e4a9d",
                                            backgroundColor: "#faf5ff"
                                        }
                                    }}
                                    onClick={handleBrowseClick}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 40, color: "#b39ddb" }} />
                                    <Typography color="textSecondary">Drag and drop file here</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Limit 200MB per file • CSV, Excel
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            mt: 2,
                                            backgroundColor: "#7e4a9d",
                                            "&:hover": { backgroundColor: "#6a3d8a" }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBrowseClick();
                                        }}
                                    >
                                        Browse files
                                    </Button>
                                    <input
                                        type="file"
                                        accept=".csv,.xlsx,.xls"
                                        ref={fileInputRef}
                                        style={{ display: "none" }}
                                        onChange={handlecurveplotFileChange}
                                    />
                                </Box>
                                {error && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        {error}
                                    </Alert>
                                )}
                                {curveplotfile && (
                                    <Alert severity="success" sx={{ mt: 2 }}>
                                        Selected file: {curveplotfile.name}
                                    </Alert>
                                )}
                            </Box>

                            {/* Plot Button */}
                            <Button
                                variant="contained"
                                onClick={curveplot}
                                disabled={!curveplotfile || loading.bess}
                                startIcon={loading.bess ? <CircularProgress size={20} color="inherit" /> : <ShowChartIcon />}
                                sx={{
                                    backgroundColor: "#7e4a9d",
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    height: "40px",
                                    fontWeight: 500,
                                    width: "auto",
                                    alignSelf: "flex-start",
                                    "&:hover": { backgroundColor: "#6a3d8a" },
                                    "&.Mui-disabled": { backgroundColor: "#d1c4e9" }
                                }}
                            >
                                {loading.bess ? "Processing..." : "Plot Data"}
                            </Button>
                            {/* Chart Display - Same style as SOH section */}
                            {formattedCurveData.length > 0 && (
                                <Paper
                                    sx={{
                                        p: 3,
                                        mt: 2,
                                        borderRadius: 3,
                                        background: "linear-gradient(145deg, #f5f0ff, #ede7f6)"
                                    }}
                                >
                                    {/* HEADER */}
                                    <Typography variant="h5" sx={{ mb: 2, color: "#5e35b1", fontWeight: "bold" }}>
                                        Consumption, PV and Consumption After PV over Time
                                    </Typography>

                                    {/* CHART */}
                                    <Paper sx={{ p: 2, height: 400 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={dailyData} margin={{ top: 10, right: 20, left: 60, bottom: 10 }}>
                                                <defs>
                                                    <linearGradient id="consumptionGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#7e4a9d" stopOpacity={0.25} />
                                                        <stop offset="95%" stopColor="#7e4a9d" stopOpacity={0} />
                                                    </linearGradient>
                                                    <linearGradient id="pvGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.25} />
                                                        <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>

                                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />

                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fontSize: 11 }}
                                                    tickFormatter={(date) => {
                                                        const d = new Date(date);
                                                        return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
                                                    }}
                                                />

                                                <YAxis
                                                    tickFormatter={(val) => Number(val).toFixed(0)}
                                                    label={{
                                                        value: "Power (kW)",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset: -45,
                                                        style: { fontWeight: 600 }
                                                    }}
                                                />

                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #ddd",
                                                        borderRadius: 8,
                                                    }}
                                                    formatter={(value, name) => [`${Number(value).toFixed(2)} kW`, name]}
                                                />

                                                <Legend verticalAlign="top" align="right" />

                                                {/* Consumption — area + line */}
                                                <Area
                                                    name="Consumption"
                                                    type="monotone"
                                                    dataKey="consumption"
                                                    stroke="#7e4a9d"
                                                    strokeWidth={2}
                                                    fill="url(#consumptionGradient)"
                                                    dot={false}
                                                />

                                                {/* PV — area + line */}
                                                <Area
                                                    name="PV"
                                                    type="monotone"
                                                    dataKey="pv"
                                                    stroke="#2e7d32"
                                                    strokeWidth={2}
                                                    fill="url(#pvGradient)"
                                                    dot={false}
                                                />

                                                {/* Consumption after PV — dashed line only */}
                                                <Line
                                                    name="Consumption after PV"
                                                    type="monotone"
                                                    dataKey="consumptionAfterPV"
                                                    stroke="#e65100"
                                                    strokeWidth={2}
                                                    strokeDasharray="6 3"
                                                    dot={false}
                                                />

                                                <Brush
                                                    dataKey="date"
                                                    height={30}
                                                    stroke="#7e4a9d"
                                                    tickFormatter={(date) => {
                                                        const d = new Date(date);
                                                        return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
                                                    }}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </Paper>

                                    {/* SUMMARY CARDS */}
                                    <Grid container spacing={2} sx={{ mt: 2 }}>
                                        {[
                                            { label: "Avg Consumption", value: `${avg.toFixed(0)} kW`, color: "#7e4a9d" },
                                            { label: "Min Consumption", value: `${min.toFixed(0)} kW`, color: "#5e35b1" },
                                            { label: "Max Consumption", value: `${max.toFixed(0)} kW`, color: "#5e35b1" },
                                        ].map((item, i) => (
                                            <Grid item xs={12} sm={4} key={i}>
                                                <Paper sx={{ p: 2, textAlign: "center", borderTop: `3px solid ${item.color}` }}>
                                                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: item.color }}>
                                                        {item.value}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>

                                </Paper>
                            )}

                            {dailymax && (
                                <Paper
                                    sx={{
                                        p: 3,
                                        mt: 2,
                                        borderRadius: 3,
                                        background: "linear-gradient(145deg, #ffffff, #ffffff)"
                                    }}>

                                    <Typography variant="h5" sx={{ mb: 2, color: "#5e35b1", fontWeight: "bold" }}>
                                        Daily Maximum Demand Occurence
                                    </Typography>

                                    <ResponsiveContainer width="100%" height={420}>
                                        <ScatterChart margin={{ top: 20, right: 40, bottom: 40, left: 20 }}>

                                            <defs>
                                                <linearGradient id="scatterGlow" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#7e4a9d" stopOpacity={0.9} />
                                                    <stop offset="100%" stopColor="#b39ddb" stopOpacity={0.2} />
                                                </linearGradient>
                                            </defs>

                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />

                                            <XAxis
                                                dataKey="time"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: "Time of Occurrence", position: "bottom" }}
                                            />

                                            <YAxis
                                                dataKey="power"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: "Power (kW)", angle: -90, position: "left" }}
                                            />

                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "#fff",
                                                    border: "1px solid #ddd",
                                                    borderRadius: 10,
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
                                                }}
                                            />

                                            <Scatter
                                                data={formattedDailyMax}
                                                fill="url(#scatterGlow)"
                                                stroke="#7e4a9d"
                                                strokeWidth={1}
                                            />
                                        </ScatterChart>
                                    </ResponsiveContainer>
                                </Paper>
                            )}


                            {averagecurve && (
                                <Paper
                                    sx={{
                                        p: 3,
                                        mt: 3,
                                        borderRadius: 3,
                                        background: "linear-gradient(145deg, #f6f3fb, #ede7f6)",
                                        border: "1px solid #e0d7ff"
                                    }}
                                >
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#7e4a9d" }}>
                                        Average Daily Load Curve (Monday to Sunday)
                                    </Typography>

                                    <ResponsiveContainer width="100%" height={420}>
                                        <LineChart
                                            data={prepareDailyCurveData(averagecurve)}
                                            margin={{ top: 10, right: 20, left: 60, bottom: 60 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                            <XAxis
                                                dataKey="time"
                                                tickFormatter={(tick) => tick}
                                                ticks={prepareDailyCurveData(averagecurve)
                                                    .map(d => d.time)
                                                    .filter(t => t && t.endsWith(":00") && t !== "00:00")  // ← exclude 00:00
                                                }
                                                tick={{ fontSize: 15 }}
                                                label={{
                                                    value: "Time of Day",
                                                    position: "insideBottom",
                                                    offset: -45,
                                                    style: { fontWeight: 600 }
                                                }}
                                            />
                                            <YAxis
                                                tickFormatter={(val) => Number(val).toFixed(2)}
                                                label={{
                                                    value: "Power (kW)",
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    offset: -45,
                                                    style: { fontWeight: 600 }
                                                }}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "#fff",
                                                    border: "1px solid #ddd",
                                                    borderRadius: 8,
                                                }}
                                                formatter={(value, name) => [
                                                    `${Number(value).toFixed(2)} kW`, name
                                                ]}
                                            />
                                            <Legend
                                                verticalAlign="top"
                                                align="right"
                                                wrapperStyle={{ paddingBottom: 10 }}
                                                formatter={(value) => (
                                                    <span style={{ fontSize: 13 }}>{value}</span>
                                                )}
                                            />
                                            {DAY_ORDER.map((day) => (
                                                <Line
                                                    key={day}
                                                    name={day}
                                                    type="monotone"
                                                    dataKey={day}
                                                    stroke={DAY_COLORS[day]}
                                                    strokeWidth={2}
                                                    dot={false}
                                                    connectNulls
                                                />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>

                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#7e4a9d" }}>
                                        Average Weekday vs Weekend Curve
                                    </Typography>

                                    <ResponsiveContainer width="100%" height={400}>
                                        <ComposedChart
                                            data={prepareAverageCurveData(averagecurve)}
                                            margin={{ top: 10, right: 20, left: 60, bottom: 60 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />

                                            <XAxis
                                                dataKey="time"
                                                tickFormatter={(tick) => tick}
                                                ticks={prepareAverageCurveData(averagecurve)
                                                    .map(d => d.time)
                                                    .filter(t => t && t.endsWith(":00") && t !== "00:00")  // ← exclude 00:00
                                                }

                                                tick={{ fontSize: 15 }}
                                                label={{
                                                    value: "Time of Day",
                                                    position: "insideBottom",
                                                    offset: -45,
                                                    style: { fontWeight: 600 }
                                                }}
                                            />
                                            <YAxis
                                                tickFormatter={(val) => Number(val).toFixed(2)}
                                            />

                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "#fff",
                                                    border: "1px solid #ddd",
                                                    borderRadius: 8,
                                                }}
                                                formatter={(value, name) => {
                                                    if (Array.isArray(value)) {
                                                        return [
                                                            `${Number(value[0]).toFixed(2)} - ${Number(value[1]).toFixed(2)} kW`,
                                                            name
                                                        ];
                                                    }
                                                    return [`${Number(value).toFixed(2)} kW`, name];
                                                }}
                                            />

                                            <Legend />

                                            {/* Weekday Range */}
                                            <Area
                                                name="Weekday Range"
                                                dataKey="weekday_range"
                                                fill="#b39ddb"
                                                fillOpacity={0.35}
                                                stroke="none"
                                            />

                                            {/* Weekend Range */}
                                            <Area
                                                name="Weekend Range"
                                                dataKey="weekend_range"
                                                fill="#90caf9"
                                                fillOpacity={0.35}
                                                stroke="none"
                                            />

                                            {/* Weekday Avg */}
                                            <Line
                                                name="Weekday Avg"
                                                type="monotone"
                                                dataKey="weekday_avg"
                                                stroke="#7e4a9d"
                                                strokeWidth={3}
                                                dot={false}
                                            />

                                            {/* Weekend Avg */}
                                            <Line
                                                name="Weekend Avg"
                                                type="monotone"
                                                dataKey="weekend_avg"
                                                stroke="#1565c0"
                                                strokeWidth={3}
                                                dot={false}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </Paper>
                            )}

                            {loadfactor && (
                                <Paper
                                    sx={{
                                        p: 3,
                                        mt: 3,
                                        borderRadius: 3,
                                        background: "linear-gradient(145deg, #f6f3fb, #ede7f6)",
                                        border: "1px solid #e0d7ff"
                                    }}
                                >
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#7e4a9d" }}>
                                        Load Factor Analysis
                                    </Typography>

                                    {/* ================= SUMMARY ================= */}
                                    <Grid container spacing={2} sx={{ mb: 3 }}>

                                        {/* Highest */}
                                        <Grid item xs={12} md={6}>
                                            <Paper
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    background: "linear-gradient(135deg, #7e4a9d 0%, #b39ddb 100%)",
                                                    color: "white"
                                                }}
                                            >
                                                <Typography variant="caption">Highest</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                                    {Number(loadfactor?.summary?.highest?.load_factor ?? 0).toFixed(3)}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {loadfactor?.summary?.highest?.date ?? "-"}
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        {/* Lowest */}
                                        <Grid item xs={12} md={6}>
                                            <Paper
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    background: "linear-gradient(135deg, #ef5350 0%, #c62828 100%)",
                                                    color: "white"
                                                }}
                                            >
                                                <Typography variant="caption">Lowest</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                                    {Number(loadfactor?.summary?.lowest?.load_factor ?? 0).toFixed(3)}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {loadfactor?.summary?.lowest?.date ?? "-"}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* ================= PERCENTILES ================= */}
                                    <Typography sx={{ mb: 1, fontWeight: 600, color: "#7e4a9d" }}>
                                        Load Factor Percentiles
                                    </Typography>

                                    <Grid container spacing={1}>
                                        {["p10", "p25", "median", "p75", "p90"].map((key) => (
                                            <Grid item xs={12} sm={6} md={2.4} key={key}>
                                                <Paper
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2,
                                                        textAlign: "center",
                                                        background:
                                                            key === "median"
                                                                ? "#f5f3f7"
                                                                : key === "p10"
                                                                    ? "#f5f3f7"
                                                                    : key === "p25"
                                                                        ? "#f1ecf6"
                                                                        : key === "p75"
                                                                            ? "#ede7f6"
                                                                            : key === "p90"
                                                                                ? "#e6def2"
                                                                                : "#ffffff"
                                                    }}
                                                >
                                                    <Typography variant="caption">{key.toUpperCase()}</Typography>

                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {Number(loadfactor.distribution.percentiles?.[key] ?? 0).toFixed(3)}
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary">
                                                        {loadfactor.distribution.percentile_dates?.[key] ?? "-"}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Typography sx={{ mb: 2, fontWeight: 600, color: "#7e4a9d", mt: 3 }}>
                                        View Daily Load Factors Table
                                    </Typography>

                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ background: "linear-gradient(135deg, #7e4a9d 0%, #b39ddb 100%)" }}>
                                                <TableCell sx={{ color: "white", fontWeight: 600 }}>Date</TableCell>
                                                <TableCell align="right" sx={{ color: "white", fontWeight: 600 }}>Mean (kW)</TableCell>
                                                <TableCell align="right" sx={{ color: "white", fontWeight: 600 }}>Max (kW)</TableCell>
                                                <TableCell align="right" sx={{ color: "white", fontWeight: 600 }}>Load Factor</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loadfactor.distribution.values?.map((row, index) => (
                                                <TableRow
                                                    key={row.date}
                                                    sx={{
                                                        background: index % 2 === 0 ? "#faf8fd" : "#f3eefb",
                                                        "&:hover": { background: "#ede7f6" }
                                                    }}
                                                >
                                                    <TableCell sx={{ fontWeight: 500 }}>{row.date}</TableCell>
                                                    <TableCell align="right">{Number(row.mean).toFixed(2)}</TableCell>
                                                    <TableCell align="right">{Number(row.max).toFixed(2)}</TableCell>
                                                    <TableCell align="right">
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: 1,
                                                                fontWeight: 600,
                                                                fontSize: "0.85rem",
                                                                // background:
                                                                //     row.load_factor >= 0.85 ? "#e8f5e9" :
                                                                //         row.load_factor >= 0.75 ? "#f3e5f5" :
                                                                //             row.load_factor >= 0.65 ? "#fff8e1" :
                                                                //                 "#ffebee",
                                                                // color:
                                                                //     row.load_factor >= 0.85 ? "#2e7d32" :
                                                                //         row.load_factor >= 0.75 ? "#6a1b9a" :
                                                                //             row.load_factor >= 0.65 ? "#f57f17" :
                                                                //                 "#c62828"
                                                            }}
                                                        >
                                                            {Number(row.load_factor).toFixed(4)}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )
            }
        </Box >
    );
}

export default BESS_Designer;